import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { ChevronDown, Plus, Trash2, MapPin, Paperclip } from "lucide-react";
import { postApi, getApi } from "../../../api/api_instance";
import { API_URLS } from "../../../api/apiUrls";
import toast from "react-hot-toast";
import { Country, State, City } from "country-state-city";

// --- SERIAL NUMBER & TREE HELPERS ---
const NON_NUMBERED_TYPES = new Set(["title", "guidance"]);

const isNumberedType = (type) =>
    type != null && !NON_NUMBERED_TYPES.has(type);

const formatSerialNumber = (prefix, index) => {
    if (index == null || index < 1) return "";
    if (!prefix) return String(index);
    return `${prefix}.${index}`;
};

const getItemOrderIndex = (items, getId) => {
    const orderIndex = new Map();
    items.forEach((item, index) => {
        const id = getId(item);
        if (id != null && !orderIndex.has(id)) {
            orderIndex.set(id, index);
        }
    });
    return orderIndex;
};

const buildChildrenByParent = (items, getId, getTriggerIds) => {
    const childrenByParent = new Map();
    const childIds = new Set();
    const OVERRIDDEN_IDS = new Set([
        "scope3_categories_capital_goods_table",
        "scope3_categories_non_capital_goods_table",
    ]);

    items.forEach((item) => {
        let triggerIds = getTriggerIds(item) || [];
        if (OVERRIDDEN_IDS.has(getId(item))) {
            triggerIds = ["scope3_first"];
        }
        triggerIds.forEach((parentId) => {
            if (!parentId) return;
            childIds.add(getId(item));
            if (!childrenByParent.has(parentId)) {
                childrenByParent.set(parentId, []);
            }
            const siblings = childrenByParent.get(parentId);
            const itemId = getId(item);
            if (!siblings.some((s) => getId(s) === itemId)) {
                siblings.push(item);
            }
        });
    });

    return { childrenByParent, childIds };
};

const getRootItems = (items, childIds, getId) =>
    items.filter((item) => !childIds.has(getId(item)));

const getQuestionTriggerIds = (question) =>
    (question.displayCondition || [])
        .map((c) => c.triggerQuestionId || c.triggerFieldId)
        .filter(Boolean);

const getFieldTriggerIds = (field) =>
    (field.displayCondition || [])
        .map((c) => c.triggerQuestionId || c.triggerFieldId)
        .filter(Boolean);

const buildQuestionTree = (questions = []) => {
    const getId = (q) => q.questionId;
    const { childrenByParent, childIds } = buildChildrenByParent(
        questions,
        getId,
        getQuestionTriggerIds,
    );
    const orderIndex = getItemOrderIndex(questions, getId);
    const roots = getRootItems(questions, childIds, getId);

    return { childrenByParent, childIds, orderIndex, roots };
};

const buildFieldTree = (fields = []) => {
    const getId = (f) => f.fieldId;
    const { childrenByParent, childIds } = buildChildrenByParent(
        fields,
        getId,
        getFieldTriggerIds,
    );
    const orderIndex = getItemOrderIndex(fields, getId);
    const roots = getRootItems(fields, childIds, getId);

    return { childrenByParent, childIds, orderIndex, roots };
};

const sortByOrderIndex = (items, orderIndex, getId) =>
    [...items].sort(
        (a, b) =>
            (orderIndex.get(getId(a)) ?? Number.MAX_SAFE_INTEGER) -
            (orderIndex.get(getId(b)) ?? Number.MAX_SAFE_INTEGER),
    );

const formatSerialDisplay = (number) => (number ? `${number}. ` : "");

const computeFieldNumbersForContext = ({
    fieldTree,
    parentQuestionNumber = "",
    isFieldVisible,
    getFieldType = (field) => field.fieldType,
    skipFieldIds = new Set(),
}) => {
    const numbers = new Map();

    const walk = (parentFieldId, parentNumber) => {
        let candidates;
        if (parentFieldId == null) {
            candidates = fieldTree.roots.filter(
                (field) => !skipFieldIds.has(field.fieldId),
            );
        } else {
            candidates = fieldTree.childrenByParent.get(parentFieldId) || [];
        }

        const visible = sortByOrderIndex(
            candidates.filter(isFieldVisible),
            fieldTree.orderIndex,
            (field) => field.fieldId,
        );

        let siblingIndex = 0;
        visible.forEach((field) => {
            let currentNumber = parentNumber;
            if (isNumberedType(getFieldType(field))) {
                siblingIndex += 1;
                currentNumber = formatSerialNumber(parentNumber, siblingIndex);
                numbers.set(field.fieldId, currentNumber);
            }
            walk(field.fieldId, currentNumber);
        });
    };

    walk(null, parentQuestionNumber || "");
    return numbers;
};

const RequiredMark = ({ show = false }) =>
    show ? <span className="text-red-500 ml-1">*</span> : null;

const FieldSerialPrefix = ({ fieldNumbers, fieldId }) => {
    const display = formatSerialDisplay(fieldNumbers?.get(fieldId));
    if (!display) return null;
    return <span className="text-teal-700 mr-2">{display}</span>;
};

// --- HOOKS & SUB-COMPONENTS ---
const useQuestionOptions = (question, onOptionsLoaded) => {
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const loadedQuestionRef = useRef(null);

    useEffect(() => {
        if (loadedQuestionRef.current === question.questionId) {
            return;
        }

        const needsFetch =
            ["select", "multiselect", "multi-select"].includes(question.questionType || question.fieldType) &&
            question.options &&
            question.options.length > 0 &&
            typeof question.options[0] === 'string';
        if (needsFetch) {
            loadedQuestionRef.current = question.questionId;
            const load = async () => {
                setLoading(true);
                try {
                    const res = await getApi({ apiUrl: API_URLS.GET_QUESTION_OPTIONS(question.questionId) });
                    if (res && res.success) {
                        setOptions(res.data);
                        if (onOptionsLoaded) {
                            onOptionsLoaded(question.questionId, res.data);
                        }
                    }
                } catch (err) {
                    console.error(`Failed options load ${question.questionId}`, err);
                }
                setLoading(false);
            };
            load();
        } else {
            const opts = question.options || [];
            loadedQuestionRef.current = question.questionId;
            setOptions(opts);
            if (onOptionsLoaded && opts.length > 0) {
                onOptionsLoaded(question.questionId, opts);
            }
        }
    }, [question.questionId, question.options, question.questionType, question.fieldType, onOptionsLoaded]);

    return { options, loading };
};

const tableDefinitionCache = new Map();

const useTableDefinition = (tableRowTypeId) => {
    const [definition, setDefinition] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!tableRowTypeId || typeof tableRowTypeId !== "string") {
            return;
        }

        if (tableDefinitionCache.has(tableRowTypeId)) {
            setDefinition(tableDefinitionCache.get(tableRowTypeId) || null);
            setLoading(false);
            return;
        }

        let cancelled = false;

        const load = async () => {
            setLoading(true);
            try {
                const res = await getApi({
                    apiUrl: API_URLS.GET_TABLE_ROW_TYPE_DEFINITION(tableRowTypeId),
                });
                if (!cancelled && res && res.success) {
                    tableDefinitionCache.set(tableRowTypeId, res.data);
                    setDefinition(res.data);
                }
            } catch (err) {
                console.error(`Failed table definition load ${tableRowTypeId}`, err);
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        load();

        return () => {
            cancelled = true;
        };
    }, [tableRowTypeId]);

    return { definition, loading };
};

const CITY_MAP = {
    uttarakhand: ["Dehradun", "Haridwar", "Roorkee", "Nainital", "Rishikesh", "Haldwani", "Rudrapur", "Almora", "Pithoragarh", "Other"],
    haryana: ["Gurugram / Gurgaon", "Faridabad", "Panipat", "Ambala", "Karnal", "Rohtak", "Hisar", "Sonipat", "Panchkula", "Yamunanagar", "Other"],
    delhi: ["New Delhi", "Central Delhi", "North Delhi", "South Delhi", "East Delhi", "West Delhi", "Other"],
    punjab: ["Ludhiana", "Amritsar", "Jalandhar", "Patiala", "Bathinda", "Mohali", "Pathankot", "Hoshiarpur", "Other"],
    uttar_pradesh: ["Noida", "Greater Noida", "Lucknow", "Kanpur", "Agra", "Varanasi", "Prayagraj", "Ghaziabad", "Meerut", "Bareilly", "Aligarh", "Other"],
    maharashtra: ["Mumbai", "Pune", "Nagpur", "Thane", "Nashik", "Aurangabad", "Navi Mumbai", "Solapur", "Kolhapur", "Other"],
    gujarat: ["Ahmedabad", "Surat", "Vadodara", "Rajkot", "Bhavnagar", "Jamnagar", "Gandhinagar", "Anand", "Other"],
    karnataka: ["Bengaluru / Bangalore", "Mysuru", "Hubballi", "Mangaluru", "Belagavi", "Davanagere", "Ballari", "Other"],
    tamil_nadu: ["Chennai", "Coimbatore", "Madurai", "Tiruchirappalli", "Salem", "Tiruppur", "Erode", "Vellore", "Other"],
    kerala: ["Thiruvananthapuram", "Kochi", "Kozhikode", "Thrissur", "Kollam", "Palakkad", "Alappuzha", "Kannur", "Other"],
    rajasthan: ["Jaipur", "Jodhpur", "Kota", "Bikaner", "Ajmer", "Udaipur", "Bhilwara", "Alwar", "Sikar", "Other"],
    west_bengal: ["Kolkata", "Howrah", "Durgapur", "Asansol", "Siliguri", "Other"],
    telangana: ["Hyderabad", "Warangal", "Nizamabad", "Karimnagar", "Other"],
    madhya_pradesh: ["Indore", "Bhopal", "Jabalpur", "Gwalior", "Ujjain", "Other"],
    bihar: ["Patna", "Gaya", "Bhagalpur", "Muzaffarpur", "Other"],
    jharkhand: ["Ranchi", "Jamshedpur", "Dhanbad", "Bokaro", "Other"],
    assam: ["Guwahati", "Silchar", "Dibrugarh", "Jorhat", "Other"],
    odisha: ["Bhubaneswar", "Cuttack", "Rourkela", "Berhampur", "Other"],
    himachal_pradesh: ["Shimla", "Dharamshala", "Mandi", "Solan", "Kullu", "Other"],
    jammu_kashmir: ["Srinagar", "Jammu", "Anantnag", "Other"],
    goa: ["Panaji", "Margao", "Vasco da Gama", "Other"]
};

const DEFAULT_CITIES = [
    "New Delhi", "Mumbai", "Bengaluru", "Kolkata", "Chennai", "Hyderabad",
    "Ahmedabad", "Pune", "Jaipur", "Lucknow", "Surat", "Kanpur", "Nagpur",
    "Indore", "Thane", "Bhopal", "Visakhapatnam", "Pimpri-Chinchwad", "Patna",
    "Vadodara", "Ghaziabad", "Ludhiana", "Agra", "Nashik", "Faridabad", "Gurugram",
    "Meerut", "Rajkot", "Varanasi", "Srinagar", "Aurangabad", "Dhanbad", "Amritsar",
    "Navi Mumbai", "Gwalior", "Jabalpur", "Coimbatore", "Vijayawada", "Jodhpur", "Madurai",
    "Raipur", "Kota", "Chandigarh", "Guwahati", "Dehradun", "Haridwar", "Other"
];

const DEFAULT_STATES = [
    "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
    "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
    "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
    "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
    "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
    "Delhi", "Jammu & Kashmir", "Ladakh", "Chandigarh", "Puducherry", "Other"
];

const COUNTRY_STATE_MAP = {
    india: DEFAULT_STATES,
    united_states: [
        "California", "Texas", "Florida", "New York", "Illinois", "Pennsylvania",
        "Ohio", "Georgia", "North Carolina", "Michigan", "New Jersey", "Virginia",
        "Washington", "Arizona", "Massachusetts", "Tennessee", "Indiana", "Missouri",
        "Maryland", "Wisconsin", "Colorado", "Minnesota", "South Carolina", "Alabama", "Other"
    ],
    united_kingdom: ["England", "Scotland", "Wales", "Northern Ireland", "Greater London", "Other"],
    uk_london: ["Greater London", "Central London", "Inner London", "Outer London", "Other"],
    australia: ["New South Wales", "Victoria", "Queensland", "Western Australia", "South Australia", "Tasmania", "Australian Capital Territory", "Northern Territory", "Other"],
    canada: ["Ontario", "Quebec", "British Columbia", "Alberta", "Manitoba", "Saskatchewan", "Nova Scotia", "New Brunswick", "Other"],
    germany: ["Bavaria", "Baden-Württemberg", "North Rhine-Westphalia", "Hesse", "Saxony", "Berlin", "Hamburg", "Lower Saxony", "Other"],
    france: ["Île-de-France", "Auvergne-Rhône-Alpes", "Nouvelle-Aquitaine", "Occitanie", "Hauts-de-France", "Provence-Alpes-Côte d'Azur", "Grand Est", "Other"],
    finland: ["Uusimaa", "Pirkanmaa", "Southwest Finland", "North Ostrobothnia", "Central Finland", "Lapland", "Satakunta", "Other"],
    china: ["Guangdong", "Shandong", "Henan", "Sichuan", "Jiangsu", "Zhejiang", "Hubei", "Hunan", "Shanghai", "Beijing", "Other"],
    japan: ["Tokyo", "Kanagawa", "Osaka", "Aichi", "Saitama", "Chiba", "Hyogo", "Hokkaido", "Fukuoka", "Other"],
    brazil: ["São Paulo", "Rio de Janeiro", "Minas Gerais", "Bahia", "Paraná", "Rio Grande do Sul", "Pernambuco", "Ceará", "Other"],
    russia: ["Moscow", "Saint Petersburg", "Moscow Oblast", "Krasnodar Krai", "Tatarstan", "Sverdlovsk Oblast", "Other"],
    russian_federation: ["Moscow", "Saint Petersburg", "Moscow Oblast", "Krasnodar Krai", "Tatarstan", "Sverdlovsk Oblast", "Other"],
    italy: ["Lombardy", "Lazio", "Campania", "Veneto", "Sicily", "Emilia-Romagna", "Piedmont", "Tuscany", "Other"],
    spain: ["Madrid", "Catalonia", "Andalusia", "Valencian Community", "Galicia", "Basque Country", "Other"],
    netherlands: ["North Holland", "South Holland", "North Brabant", "Utrecht", "Gelderland", "Overijssel", "Other"],
    switzerland: ["Zurich", "Berne", "Vaud", "Aargau", "St. Gallen", "Geneva", "Other"],
    united_arab_emirates: ["Abu Dhabi", "Dubai", "Sharjah", "Ajman", "Ras Al Khaimah", "Fujairah", "Umm Al Quwain", "Other"],
    saudi_arabia: ["Riyadh", "Makkah", "Eastern Province", "Madinah", "Asir", "Other"],
    singapore: ["Central Region", "East Region", "North Region", "North-East Region", "West Region", "Other"],
    south_africa: ["Gauteng", "KwaZulu-Natal", "Western Cape", "Eastern Cape", "Limpopo", "Mpumalanga", "Other"],
    mexico: ["Mexico City", "Jalisco", "Nuevo León", "State of Mexico", "Puebla", "Guanajuato", "Other"],
    argentina: ["Buenos Aires", "Córdoba", "Santa Fe", "Mendoza", "Tucumán", "Other"],
    austria: ["Vienna", "Lower Austria", "Upper Austria", "Styria", "Tyrol", "Other"],
    belgium: ["Flanders", "Wallonia", "Brussels", "Other"],
    chile: ["Santiago Metropolitan", "Valparaíso", "Biobío", "Other"],
    colombia: ["Bogotá", "Antioquia", "Valle del Cauca", "Cundinamarca", "Other"],
    costa_rica: ["San José", "Alajuela", "Cartago", "Heredia", "Other"],
    czech_republic: ["Prague", "Central Bohemian", "South Moravian", "Moravian-Silesian", "Other"],
    egypt: ["Cairo", "Giza", "Alexandria", "Dakahlia", "Other"],
    fiji: ["Central", "Western", "Northern", "Eastern", "Other"],
    greece: ["Attica", "Central Macedonia", "Crete", "Thessaly", "Other"],
    hong_kong_china: ["Hong Kong Island", "Kowloon", "New Territories", "Other"],
    indonesia: ["Jakarta", "West Java", "East Java", "Central Java", "Banten", "North Sumatra", "Other"],
    ireland: ["Dublin", "Cork", "Galway", "Limerick", "Kildare", "Other"],
    israel: ["Center District", "Tel Aviv", "Haifa", "Northern District", "Southern District", "Jerusalem", "Other"],
    jordan: ["Amman", "Irbid", "Zarqa", "Balqa", "Other"],
    kazakhstan: ["Almaty", "Astana", "Shymkent", "Karaganda", "Other"],
    korea: ["Seoul", "Gyeonggi-do", "Busan", "Incheon", "Gyeongsangnam-do", "Other"],
    south_korea: ["Seoul", "Gyeonggi-do", "Busan", "Incheon", "Gyeongsangnam-do", "Other"],
    macau_china: ["Macau Peninsula", "Taipa", "Coloane", "Other"],
    malaysia: ["Selangor", "Kuala Lumpur", "Johor", "Penang", "Sabah", "Sarawak", "Other"],
    maldives: ["Malé", "Addu City", "Fuvahmulah", "Kulhudhuffushi", "Other"],
    new_zealand: ["Auckland", "Canterbury", "Wellington", "Waikato", "Bay of Plenty", "Other"],
    oman: ["Muscat", "Dhofar", "Al Batinah North", "Dakhiliyah", "Other"],
    panama: ["Panama", "West Panama", "Chiriquí", "Colón", "Other"],
    peru: ["Lima", "Arequipa", "La Libertad", "Piura", "Cusco", "Other"],
    philippines: ["Metro Manila", "CALABARZON", "Central Luzon", "Central Visayas", "Western Visayas", "Other"],
    poland: ["Masovian", "Silesian", "Greater Poland", "Lesser Poland", "Lower Silesian", "Other"],
    portugal: ["Lisbon", "Porto", "Setúbal", "Braga", "Aveiro", "Other"],
    qatar: ["Doha", "Al Rayyan", "Al Wakrah", "Al Khor", "Other"],
    romania: ["Bucharest", "Cluj", "Timiș", "Iași", "Constanța", "Other"],
    thailand: ["Bangkok", "Nonthaburi", "Nakhon Ratchasima", "Chiang Mai", "Chonburi", "Other"],
    turkey: ["Istanbul", "Ankara", "Izmir", "Bursa", "Antalya", "Other"],
    vietnam: ["Ho Chi Minh City", "Hanoi", "Da Nang", "Binh Duong", "Dong Nai", "Other"],
    morocco: ["Casablanca-Settat", "Rabat-Salé-Kénitra", "Tanger-Tetouan-Al Hoceima", "Marrakesh-Safi", "Other"],
    hungary: ["Budapest", "Pest", "Borsod-Abaúj-Zemplén", "Győr-Moson-Sopron", "Other"]
};

const getStateListForCountry = (countryVal) => {
    if (!countryVal) return COUNTRY_STATE_MAP.india;
    const str = extractStateString(countryVal);
    
    const allCountries = Country.getAllCountries();
    const countryStr = str.toLowerCase();
    const foundCountry = allCountries.find(c => c.name.toLowerCase() === countryStr || c.isoCode.toLowerCase() === countryStr);
    
    if (foundCountry) {
        const states = State.getStatesOfCountry(foundCountry.isoCode);
        if (states && states.length > 0) {
            return [...states.map(s => s.name), "Other"];
        }
    }

    const cleanCountry = str.toLowerCase().replace(/[^a-z0-9]/g, "");
    if (!cleanCountry) return COUNTRY_STATE_MAP.india;

    for (const key of Object.keys(COUNTRY_STATE_MAP)) {
        const cleanKey = key.toLowerCase().replace(/[^a-z0-9]/g, "");
        if (cleanCountry === cleanKey || cleanCountry.includes(cleanKey) || cleanKey.includes(cleanCountry)) {
            return COUNTRY_STATE_MAP[key];
        }
    }

    return ["Other"];
};

const STATE_ALIASES = {
    up: "uttar_pradesh",
    uttarpradesh: "uttar_pradesh",
    uk: "uttarakhand",
    uttarakhand: "uttarakhand",
    uttaranchal: "uttarakhand",
    hr: "haryana",
    haryana: "haryana",
    dl: "delhi",
    delhi: "delhi",
    newdelhi: "delhi",
    pb: "punjab",
    punjab: "punjab",
    mh: "maharashtra",
    maharashtra: "maharashtra",
    gj: "gujarat",
    gujarat: "gujarat",
    ka: "karnataka",
    karnataka: "karnataka",
    tn: "tamil_nadu",
    tamilnadu: "tamil_nadu",
    kl: "kerala",
    kerala: "kerala",
    rj: "rajasthan",
    rajasthan: "rajasthan",
    wb: "west_bengal",
    westbengal: "west_bengal",
    ts: "telangana",
    telangana: "telangana",
    mp: "madhya_pradesh",
    madhyapradesh: "madhya_pradesh",
    br: "bihar",
    bihar: "bihar",
    jh: "jharkhand",
    jharkhand: "jharkhand",
    as: "assam",
    assam: "assam",
    od: "odisha",
    orissa: "odisha",
    odisha: "odisha",
    hp: "himachal_pradesh",
    himachalpradesh: "himachal_pradesh",
    jk: "jammu_kashmir",
    jammukashmir: "jammu_kashmir",
    ga: "goa",
    goa: "goa",
    cg: "chhattisgarh",
    chhattisgarh: "chhattisgarh",
    chattisgarh: "chhattisgarh"
};

const extractStateString = (val) => {
    if (!val) return "";
    if (typeof val === "string") return val;
    if (typeof val === "object" && !Array.isArray(val)) {
        return val.value || val.label || val.name || "";
    }
    if (Array.isArray(val) && val.length > 0) {
        return extractStateString(val[0]);
    }
    return String(val);
};

const getCityListForState = (stateVal, countryVal) => {
    const isIndia = !countryVal || String(countryVal).toLowerCase().includes("india");
    const fallbackCities = ["Other"];

    if (!stateVal) return isIndia ? DEFAULT_CITIES : fallbackCities;
    const str = extractStateString(stateVal);
    
    // Attempt to match with country-state-city
    const allCountries = Country.getAllCountries();
    let targetCountryCode = 'IN';
    if (countryVal) {
        const countryStr = extractStateString(countryVal).toLowerCase();
        const foundCountry = allCountries.find(c => c.name.toLowerCase() === countryStr || c.isoCode.toLowerCase() === countryStr);
        if (foundCountry) {
            targetCountryCode = foundCountry.isoCode;
        }
    }
    
    const allStatesInCountry = State.getStatesOfCountry(targetCountryCode);
    const stateStrLower = str.toLowerCase();
    
    // Find matching state by exact name, fuzzy match, or isoCode
    const foundState = allStatesInCountry.find(s => 
        s.name.toLowerCase() === stateStrLower || 
        s.isoCode.toLowerCase() === stateStrLower ||
        s.name.toLowerCase().includes(stateStrLower) ||
        stateStrLower.includes(s.name.toLowerCase())
    );

    if (foundState) {
        const cities = City.getCitiesOfState(targetCountryCode, foundState.isoCode);
        if (cities && cities.length > 0) {
            return [...cities.map(c => c.name), "Other"];
        }
    }

    // Fallback to legacy Indian cities map if the above failed
    const cleanState = str.toLowerCase().replace(/[^a-z0-9]/g, "");

    if (STATE_ALIASES[cleanState]) {
        const canonicalKey = STATE_ALIASES[cleanState];
        if (CITY_MAP[canonicalKey]) {
            return CITY_MAP[canonicalKey];
        }
    }

    for (const key of Object.keys(CITY_MAP)) {
        const cleanKey = key.toLowerCase().replace(/[^a-z0-9]/g, "");
        if (cleanState === cleanKey || cleanState.includes(cleanKey) || cleanKey.includes(cleanState)) {
            return CITY_MAP[key];
        }
    }
    
    return isIndia ? DEFAULT_CITIES : fallbackCities;
};

const QuestionInput = ({
    field,
    value,
    onChange,
    onBlur,
    onOptionsLoaded,
    disabled = false,
    rowData = {},
}) => {
    const [error, setError] = useState("");
    const [isFocused, setIsFocused] = useState(false);
    const { options, loading } = useQuestionOptions(field, onOptionsLoaded);
    const type = field?.questionType || field?.fieldType;

    const disabledClassName = disabled
        ? "bg-gray-100 cursor-not-allowed text-gray-500"
        : "";

    const isPercentage =
        type === "percentage" ||
        String(field?.questionText || field?.fieldLabel || "")
            .toLowerCase()
            .includes("percentage");

    if (loading) {
        return <span className="text-gray-400 text-sm">Loading options...</span>;
    }

    switch (type) {
        case "text": {
            const fieldId = String(field?.fieldId || field?.questionId || "").toLowerCase();
            const fieldLabel = String(field?.fieldLabel || field?.questionText || "").toLowerCase();
            const isCityField = fieldId === "city" || fieldId.includes("city") || fieldLabel.includes("city");
            const isStateField = fieldId === "state" || fieldId.includes("state") || fieldLabel.includes("state");

            let effectiveOptions = options;

            if (isCityField) {
                let selectedState = rowData?.india_state || rowData?.australia_state || rowData?.usa_state || rowData?.state;
                if (!selectedState) {
                    for (const [k, v] of Object.entries(rowData || {})) {
                        if (k.toLowerCase().includes("state") && v) {
                            selectedState = v;
                            break;
                        }
                    }
                }
                
                let selectedCountry = rowData?.country || rowData?.corporate_headquarter_country;
                if (!selectedCountry) {
                    for (const [k, v] of Object.entries(rowData || {})) {
                        if (k.toLowerCase().includes("country") && v) {
                            selectedCountry = v;
                            break;
                        }
                    }
                }

                effectiveOptions = getCityListForState(selectedState, selectedCountry);
            } else if (isStateField) {
                let selectedCountry = rowData?.country || rowData?.corporate_headquarter_country;
                if (!selectedCountry && rowData && typeof rowData === "object") {
                    for (const [k, v] of Object.entries(rowData)) {
                        if (k.toLowerCase().includes("country") && v) {
                            selectedCountry = v;
                            break;
                        }
                    }
                }
                effectiveOptions = getStateListForCountry(selectedCountry);
            }

            if (effectiveOptions && effectiveOptions.length > 0) {
                const placeholder = isCityField ? "-- Select City --" : isStateField ? "-- Select State --" : "-- Select --";
                
                const stringVal = String(value || "");
                const isKnownOption = effectiveOptions.some((opt) => {
                    const val = typeof opt === "object" ? (opt?.value ?? opt?.label) : opt;
                    return String(val).toLowerCase() === stringVal.toLowerCase();
                });

                const isOtherSelected =
                    stringVal === "Other" ||
                    stringVal === "other" ||
                    stringVal.startsWith("Other:") ||
                    (!isKnownOption && stringVal !== "");

                const customTextValue = stringVal.startsWith("Other:")
                    ? stringVal.replace(/^Other:\s*/, "")
                    : isKnownOption
                        ? ""
                        : stringVal === "Other" || stringVal === "other"
                            ? ""
                            : stringVal;

                return (
                    <div className="space-y-2">
                        <select
                            value={isOtherSelected ? "Other" : value || ""}
                            disabled={disabled}
                            onChange={(e) => {
                                const selected = e.target.value;
                                if (selected === "Other") {
                                    onChange("Other");
                                } else {
                                    onChange(selected);
                                }
                            }}
                            onBlur={() => onBlur?.()}
                            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white outline-none ${disabledClassName}`}
                        >
                            <option value="">{placeholder}</option>
                            {effectiveOptions.map((opt) => {
                                const val = typeof opt === "object" ? (opt?.value ?? opt?.label) : opt;
                                const lbl = typeof opt === "object" ? (opt?.label ?? opt?.value) : opt;
                                return (
                                    <option key={val} value={val}>
                                        {lbl}
                                    </option>
                                );
                            })}
                        </select>

                        {isOtherSelected && (
                            <input
                                type="text"
                                value={customTextValue}
                                disabled={disabled}
                                onChange={(e) =>
                                    onChange(e.target.value ? `Other: ${e.target.value}` : "Other")
                                }
                                onBlur={() => onBlur?.()}
                                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none mt-2 ${disabledClassName}`}
                                placeholder={`Specify Other ${isCityField ? "City" : isStateField ? "State" : "Value"}`}
                            />
                        )}
                    </div>
                );
            }
            return (
                <input
                    type="text"
                    value={value || ""}
                    disabled={disabled}
                    onChange={(e) => onChange(e.target.value)}
                    onBlur={() => onBlur?.()}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none ${disabledClassName}`}
                    placeholder={`Enter ${field?.questionText || field?.fieldLabel || "value"}`}
                />
            );
        }

        case "date":
            return (
                <input
                    type="date"
                    value={value || ""}
                    disabled={disabled}
                    onChange={(e) => onChange(e.target.value)}
                    onBlur={() => onBlur?.()}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none ${disabledClassName}`}
                />
            );

        case "number":
        case "percentage": {
            const rangeLimit =
                Array.isArray(field?.limit) && field.limit.length > 0
                    ? field.limit[0]
                    : null;

            const showPercentageSuffix =
                isPercentage &&
                !isFocused &&
                value !== "" &&
                value !== null &&
                value !== undefined;
            const displayValue = showPercentageSuffix ? `${value}%` : (value ?? "");
            const inputType = showPercentageSuffix ? "text" : "number";

            return (
                <div className="relative w-full">
                    <input
                        type={inputType}
                        min="0"
                        value={displayValue}
                        disabled={disabled}
                        onChange={(e) => {
                            let val = e.target.value;
                            if (val !== "") {
                                const numVal = Number(val);

                                if (numVal < 0) {
                                    toast.error("Negative values are not allowed");
                                    return;
                                }

                                if (isPercentage && numVal > 100) {
                                    toast.error("Percentage cannot exceed 100%");
                                    val = "100";
                                }

                                if (
                                    rangeLimit &&
                                    typeof rangeLimit === "string" &&
                                    rangeLimit.includes("-")
                                ) {
                                    const [min, max] = rangeLimit.split("-").map(Number);
                                    if (numVal < min) {
                                        toast.error(`Value cannot be less than ${min}`);
                                        val = min.toString();
                                    } else if (numVal > max) {
                                        toast.error(`Value cannot exceed ${max}`);
                                        val = max.toString();
                                    }
                                }
                            }

                            onChange(val);
                        }}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => {
                            setIsFocused(false);
                            onBlur?.();
                        }}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none ${disabledClassName}`}
                        placeholder={
                            rangeLimit
                                ? `Range ${rangeLimit}`
                                : isPercentage
                                    ? "Enter in percentage"
                                    : "Enter value"
                        }
                    />
                </div>
            );
        }

        case "select":
            return (
                <select
                    value={value || ""}
                    disabled={disabled}
                    onChange={(e) => onChange(e.target.value)}
                    onBlur={() => onBlur?.()}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white outline-none ${disabledClassName}`}
                >
                    <option value="">-- Select --</option>
                    {options.map((opt) => (
                        <option key={opt?._id || opt?.value} value={opt?.value}>
                            {opt?.label}
                        </option>
                    ))}
                </select>
            );

        case "multiselect":
        case "multi-select": {
            const current = Array.isArray(value) ? value : [];
            const hasHeaders = options.some((opt) => !opt?.value || opt.value === "");

            if (!hasHeaders) {
                return (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {options.map((opt) => (
                            <label
                                key={opt?._id || opt?.value}
                                className={`flex items-center gap-2 p-2 rounded ${
                                    disabled
                                        ? "cursor-not-allowed opacity-60"
                                        : "cursor-pointer hover:bg-gray-50"
                                }`}
                            >
                                <input
                                    type="checkbox"
                                    checked={current.includes(opt?.value)}
                                    disabled={disabled}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            onChange([...current, opt?.value]);
                                        } else {
                                            onChange(current.filter((v) => v !== opt?.value));
                                        }
                                    }}
                                    className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500"
                                />
                                <span className="text-gray-700 text-sm">{opt?.label}</span>
                            </label>
                        ))}
                    </div>
                );
            }

            const groupedOptions = [];
            let currentGroup = { header: null, options: [] };

            options.forEach((opt) => {
                if (!opt?.value || opt.value === "") {
                    if (currentGroup.options.length > 0) {
                        groupedOptions.push(currentGroup);
                    }
                    currentGroup = { header: opt?.label, options: [] };
                } else {
                    currentGroup.options.push(opt);
                }
            });

            if (currentGroup.options.length > 0) {
                groupedOptions.push(currentGroup);
            }

            return (
                <div className="space-y-4">
                    {groupedOptions.map((group, groupIdx) => (
                        <div key={groupIdx} className="space-y-1">
                            {group.header && (
                                <div className="font-semibold text-gray-800 text-sm uppercase tracking-wide">
                                    {group.header}
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-0">
                                {group.options.map((opt) => (
                                    <label
                                        key={opt?._id || opt?.value}
                                        className={`flex items-center gap-2 p-1 rounded ${
                                            disabled
                                                ? "cursor-not-allowed opacity-60"
                                                : "cursor-pointer hover:bg-gray-50"
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={current.includes(opt?.value)}
                                            disabled={disabled}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    onChange([...current, opt?.value]);
                                                } else {
                                                    onChange(current.filter((v) => v !== opt?.value));
                                                }
                                            }}
                                            className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500 flex-shrink-0"
                                        />
                                        <span className="text-gray-700 text-sm">{opt?.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            );
        }
 case "file": {
            const files = Array.isArray(value) ? value : value ? [value] : [];
            return (
                <div className="space-y-3">
                    <input
                        type="file"
                        multiple
                        disabled={disabled}
                        onChange={(e) => {
                            if (disabled) return;
                            if (e.target.files && e.target.files.length > 0) {
                                const newFiles = Array.from(e.target.files);
                                onChange([...files, ...newFiles]);
                                e.target.value = null;
                            }
                        }}
                        className={`block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100 ${
                            disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
                        }`}
                    />
                    {files.length > 0 && (
                        <div className="space-y-2">
                            <p className="text-sm font-medium text-gray-700">
                                Uploaded Files:
                            </p>
                            <ul className="space-y-1">
                                {files.map((file, idx) => (
                                    <li
                                        key={idx}
                                        className="flex items-center justify-between bg-white px-3 py-2 rounded-lg border border-gray-200 shadow-sm"
                                    >
                                        <div className="flex items-center gap-2 overflow-hidden">
                                            <Paperclip size={14} className="text-teal-600 flex-shrink-0" />
                                            <span className="text-xs sm:text-sm text-gray-700 truncate">
                                                {file?.name ||
                                                    (typeof file === "string"
                                                        ? file.split("/").pop()
                                                        : "View File")}
                                            </span>
                                        </div>
                                        {!disabled && (
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    onChange(files.filter((_, i) => i !== idx))
                                                }
                                                className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors ml-2 flex-shrink-0"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            );
        }
        default:
            return null;
    }
};

const FormQuestionTable = ({ renderTable }) => {
    return (
        <div className="mt-4">
            {renderTable()}
        </div>
    );
};

const TableManager = ({
    question,
    tableId,
    answers,
    setAnswers,
    evalCondition,
    tripIndex,
    onLoadDefinition,
    parentMandatory = false,
    parentQuestionNumber = "",
    readOnly = false,
    questionsList = [],
}) => {
    const { definition, loading } = useTableDefinition(question.tableRowTypeId);

    useEffect(() => {
        if (definition && onLoadDefinition) {
            onLoadDefinition(definition);
        }
    }, [definition, onLoadDefinition, question.questionId]);

    const rawFields = definition?.fields || [];
    const fields = useMemo(() => {
        const hasState = rawFields.some(f => f.fieldId.toLowerCase().includes("state") || String(f.fieldLabel).toLowerCase().includes("state"));
        const hasCity = rawFields.some(f => f.fieldId.toLowerCase().includes("city") || String(f.fieldLabel).toLowerCase().includes("city"));
        if (hasState && !hasCity) {
            const stateField = rawFields.find(f => f.fieldId.toLowerCase().includes("state") || String(f.fieldLabel).toLowerCase().includes("state"));
            const prefix = stateField.fieldId.includes("_state") ? stateField.fieldId.replace(/_state.*$/, "") : "";
            const cityFieldId = prefix ? `${prefix}_city` : "city";
            return [
                ...rawFields,
                {
                    fieldId: cityFieldId,
                    fieldLabel: "City",
                    fieldType: "text",
                    displayCondition: [
                        {
                            triggerQuestionId: stateField.fieldId,
                            operator: "exists",
                            value: true
                        }
                    ]
                }
            ];
        }
        return rawFields;
    }, [rawFields]);

    const fieldTree = useMemo(() => buildFieldTree(fields), [fields]);

    const categoryFields = useMemo(
        () =>
            fields.filter(
                (f) =>
                    ["multiselect", "multi-select"].includes(f.fieldType) &&
                    (!f.displayCondition || f.displayCondition.length === 0) &&
                    fields.some((other) =>
                        other.displayCondition?.some(
                            (condition) =>
                                (condition.triggerQuestionId || condition.triggerFieldId) ===
                                f.fieldId,
                        ),
                    ),
            ),
        [fields],
    );

    const isCategorized = categoryFields.length > 0;

    const categoryFieldIds = useMemo(
        () => new Set(categoryFields.map((field) => field.fieldId)),
        [categoryFields],
    );

    const belongsToField = (rowKey, fieldId) => {
        if (rowKey === fieldId) return true;
        if (rowKey.startsWith(`${fieldId}_`)) {
            return !fields.some(
                (f) =>
                    f.fieldId !== fieldId &&
                    f.fieldId.length > fieldId.length &&
                    rowKey.startsWith(f.fieldId),
            );
        }
        return false;
    };

    const getFieldNumbersForRow = (rowContext) =>
        computeFieldNumbersForContext({
            fieldTree,
            parentQuestionNumber,
            isFieldVisible: (field) => evalCondition(field, { rowData: rowContext }),
            skipFieldIds: categoryFieldIds,
        });

    const selectedCategories = useMemo(() => {
        if (!isCategorized) return ["default"];
        const allCats = new Set();
        categoryFields.forEach((cf) => {
            const vals = answers[`${tableId}_${cf.fieldId}`] || [];
            if (Array.isArray(vals)) {
                vals.forEach((v) => allCats.add(v));
            }
        });
        return Array.from(allCats);
    }, [categoryFields, answers, tableId, isCategorized]);

    useEffect(() => {
        if (!definition || loading) return;
        setAnswers((prev) => {
            const tableData = prev[tableId] || {};
            let changed = false;
            const newTableData = { ...tableData };
            selectedCategories.forEach((catKey) => {
                if (!newTableData[catKey] || newTableData[catKey].length === 0) {
                    newTableData[catKey] = [{ rowId: "row_1", values: {} }];
                    changed = true;
                }
            });
            if (changed) return { ...prev, [tableId]: newTableData };
            return prev;
        });
    }, [
        tableId,
        selectedCategories.join(","),
        definition,
        loading,
        setAnswers,
        answers[tableId],
    ]);

    const collectTableDescendantFieldIds = (
        parentFieldId,
        collected = new Set(),
    ) => {
        fields.forEach((field) => {
            if (
                field.displayCondition?.some(
                    (condition) =>
                        (condition.triggerQuestionId || condition.triggerFieldId) ===
                        parentFieldId,
                )
            ) {
                if (!collected.has(field.fieldId)) {
                    collected.add(field.fieldId);
                    collectTableDescendantFieldIds(field.fieldId, collected);
                }
            }
        });

        return collected;
    };

    const collectVisibleTableFieldKeys = (
        parentFieldId,
        rowValues,
        visibleKeys = new Set(),
    ) => {
        const visibleChildren = fields.filter((field) => {
            if (!field.displayCondition) return false;

            const parentConditions = field.displayCondition.filter(
                (condition) =>
                    (condition.triggerQuestionId || condition.triggerFieldId) ===
                    parentFieldId,
            );

            if (parentConditions.length === 0) return false;

            const logicalOp = (field.logicalOperator || "AND").toUpperCase();
            const isSatisfied = (condition) =>
                evalCondition(condition, {
                    rowData: rowValues,
                });

            if (logicalOp === "OR") {
                return parentConditions.some(isSatisfied);
            }

            return parentConditions.some(isSatisfied);
        });

        visibleChildren.forEach((field) => {
            visibleKeys.add(field.fieldId);

            Object.keys(rowValues).forEach((rowKey) => {
                if (belongsToField(rowKey, field.fieldId)) {
                    visibleKeys.add(rowKey);
                }
            });

            collectVisibleTableFieldKeys(field.fieldId, rowValues, visibleKeys);
        });

        return visibleKeys;
    };

    const clearHiddenTableDescendantValues = (parentFieldId, rowValues) => {
        const descendantFieldIds = collectTableDescendantFieldIds(parentFieldId);
        if (descendantFieldIds.size === 0) return rowValues;

        const visibleKeys = collectVisibleTableFieldKeys(
            parentFieldId,
            rowValues,
            new Set(),
        );

        const nextRowValues = { ...rowValues };

        Object.keys(nextRowValues).forEach((rowKey) => {
            const belongsToDescendant = Array.from(descendantFieldIds).some(
                (fieldId) => belongsToField(rowKey, fieldId),
            );

            if (!belongsToDescendant) return;

            const stillVisible = Array.from(visibleKeys).some((visibleKey) =>
                belongsToField(rowKey, visibleKey),
            );

            if (!stillVisible) {
                delete nextRowValues[rowKey];
            }
        });

        return nextRowValues;
    };

    const parseLoopKey = (key) => {
        const match = key.match(/_loop_(\d+)/);
        return match ? { loopIndex: Number(match[1]) } : null;
    };

    const getLoopContext = (loopIndex, rowValues) => {
        const ctx = { ...rowValues };
        fields.forEach((f) => {
            const loopKey = `${f.fieldId}_loop_${loopIndex}`;
            if (rowValues[loopKey] !== undefined) {
                ctx[f.fieldId] = rowValues[loopKey];
            }
        });
        return ctx;
    };

    const setTableRow = (catKey, rowId, rowData) => {
        if (readOnly) return;

        let updatedRowData = { ...rowData };
        const tableData = answers[tableId] || {};
        const groupRows = tableData[catKey] || [];
        const existingRow = groupRows.find((r) => r.rowId === rowId);

        const changedFieldIds = fields
            .filter((field) => {
                if (categoryFieldIds.has(field.fieldId)) return false;

                const fieldId = field.fieldId;
                const previousEntries = Object.entries(
                    existingRow?.values || {},
                ).filter(([key]) => belongsToField(key, fieldId));
                const nextEntries = Object.entries(updatedRowData).filter(([key]) =>
                    belongsToField(key, fieldId),
                );

                return JSON.stringify(previousEntries) !== JSON.stringify(nextEntries);
            })
            .map((field) => field.fieldId);

        // Validation: Waste sent for recycling cannot exceed Total recyclable waste
        if (
            updatedRowData.recyclable_waste !== undefined &&
            updatedRowData.waste_sent_recycling !== undefined
        ) {
            const total = parseFloat(updatedRowData.recyclable_waste);
            const sent = parseFloat(updatedRowData.waste_sent_recycling);
            if (!isNaN(total) && !isNaN(sent) && sent > total) {
                toast.error("Waste sent for recycling cannot exceed Total recyclable waste!");
                const changedFieldKey = changedFieldIds[0];
                if (changedFieldKey) {
                    updatedRowData[changedFieldKey] =
                        existingRow?.values?.[changedFieldKey] !== undefined
                            ? existingRow.values[changedFieldKey]
                            : "";
                }
            }
        }

        // Validation for looped keys
        const loopKeys = Object.keys(updatedRowData).filter((k) =>
            k.startsWith("recyclable_waste_loop_"),
        );
        for (const key of loopKeys) {
            const suffix = key.replace("recyclable_waste_loop_", "");
            const sentKey = `waste_sent_recycling_loop_${suffix}`;
            if (updatedRowData[sentKey] !== undefined) {
                const total = parseFloat(updatedRowData[key]);
                const sent = parseFloat(updatedRowData[sentKey]);
                if (!isNaN(total) && !isNaN(sent) && sent > total) {
                    toast.error("Waste sent for recycling cannot exceed Total recyclable waste!");
                    const changedFieldKey = changedFieldIds[0];
                    if (changedFieldKey) {
                        updatedRowData[changedFieldKey] =
                            existingRow?.values?.[changedFieldKey] !== undefined
                                ? existingRow.values[changedFieldKey]
                                : "";
                    }
                }
            }
        }



        const clearHiddenLoopDescendantValues = (
            parentFieldId,
            loopIndex,
            rowValues,
        ) => {
            const descendantFieldIds = collectTableDescendantFieldIds(parentFieldId);
            if (descendantFieldIds.size === 0) return rowValues;

            const loopCtx = getLoopContext(loopIndex, rowValues);
            const visibleKeys = collectVisibleTableFieldKeys(
                parentFieldId,
                loopCtx,
                new Set(),
            );

            const nextRowValues = { ...rowValues };
            const suffix = `_loop_${loopIndex}`;

            Object.keys(nextRowValues).forEach((rowKey) => {
                if (!rowKey.includes(suffix)) return;

                const belongsToDescendant = Array.from(descendantFieldIds).some(
                    (fieldId) =>
                        rowKey === `${fieldId}${suffix}` ||
                        rowKey.startsWith(`${fieldId}${suffix}_`),
                );
                if (!belongsToDescendant) return;

                const stillVisible = Array.from(visibleKeys).some(
                    (visibleKey) =>
                        rowKey === `${visibleKey}${suffix}` ||
                        rowKey.startsWith(`${visibleKey}${suffix}_`),
                );

                if (!stillVisible) {
                    delete nextRowValues[rowKey];
                }
            });

            return nextRowValues;
        };

        changedFieldIds.forEach((fieldId) => {
            const relevantKeys = Object.keys(updatedRowData).filter((k) =>
                belongsToField(k, fieldId),
            );
            const loopIndices = new Set(
                relevantKeys
                    .map((k) => parseLoopKey(k)?.loopIndex)
                    .filter((v) => v !== undefined),
            );
            if (loopIndices.size > 0) {
                loopIndices.forEach((idx) => {
                    updatedRowData = clearHiddenLoopDescendantValues(
                        fieldId,
                        idx,
                        updatedRowData,
                    );
                });
            } else {
                updatedRowData = clearHiddenTableDescendantValues(
                    fieldId,
                    updatedRowData,
                );
            }

            const changedField = fields.find((f) => f.fieldId === fieldId);
            if (
                changedField &&
                ["multiselect", "multi-select"].includes(changedField.fieldType)
            ) {
                const prevValues = Array.isArray(existingRow?.values?.[fieldId])
                    ? existingRow.values[fieldId]
                    : [];
                const nextValues = Array.isArray(updatedRowData[fieldId])
                    ? updatedRowData[fieldId]
                    : [];
                const deselectedOptions = prevValues.filter(
                    (v) => !nextValues.includes(v),
                );

                if (deselectedOptions.length > 0) {
                    Object.keys(updatedRowData).forEach((key) => {
                        const isOrphanedSuffix = deselectedOptions.some(
                            (opt) =>
                                key.endsWith(`_${opt}`) ||
                                key.includes(`_${opt}_`) ||
                                (key.includes(`_loop_`) && key.includes(`_${opt}`)),
                        );
                        if (isOrphanedSuffix) {
                            delete updatedRowData[key];
                        }
                    });
                }
            }
        });

        // Calculation groups
        const calculationGroups = {};

        fields.forEach((field) => {
            if (field.resultAction) {
                const action = field.resultAction;
                const sourceFields = fields.filter(
                    (f) =>
                        f.calculationType &&
                        Array.isArray(f.calculationType) &&
                        f.calculationType.includes(action),
                );

                if (sourceFields.length > 0) {
                    if (!calculationGroups[action]) {
                        calculationGroups[action] = [];
                    }
                    calculationGroups[action].push({
                        targetField: field.fieldId,
                        sourceFields: sourceFields.map((f) => f.fieldId),
                        action: action,
                    });
                }
            }
        });

        Object.values(calculationGroups).forEach((calculations) => {
            calculations.forEach(({ targetField, sourceFields, action }) => {
                let total = 0;
                let count = 0;
                let hasData = false;

                sourceFields.forEach((sourceId) => {
                    const value = updatedRowData[sourceId];
                    if (value !== undefined && value !== null && value !== "") {
                        const numValue = parseFloat(value) || 0;
                        total += numValue;
                        count++;
                        hasData = true;
                    }
                });

                if (hasData) {
                    if (action === "total") {
                        updatedRowData[targetField] = total;
                    } else if (action === "avg") {
                        updatedRowData[targetField] =
                            count > 0 ? Number((total / count).toFixed(4)) : 0;
                    }
                } else {
                    updatedRowData[targetField] = "";
                }
            });
        });

        setAnswers((prev) => {
            const nextTableData = prev[tableId] || {};
            const nextGroupRows = nextTableData[catKey] || [];
            const idx = nextGroupRows.findIndex((r) => r.rowId === rowId);
            const updated = [...nextGroupRows];
            if (idx >= 0) updated[idx] = { ...updated[idx], values: updatedRowData };
            else updated.push({ rowId, values: updatedRowData });
            return { ...prev, [tableId]: { ...nextTableData, [catKey]: updated } };
        });
    };

    const getMultiselectDependency = (field, rowData) => {
        const multiselectDeps = field.displayCondition?.filter((cond) => {
            if (cond.operator !== "exists" && cond.operator !== "exist") return false;

            const triggerId = cond.triggerQuestionId || cond.triggerFieldId;

            if (
                isCategorized &&
                categoryFields.some((cf) => cf.fieldId === triggerId)
            ) {
                return false;
            }

            const triggerField =
                fields.find((f) => f.fieldId === triggerId) ||
                questionsList?.find((q) => q.questionId === triggerId);
            return (
                triggerField?.fieldType === "multiselect" ||
                triggerField?.fieldType === "multi-select" ||
                triggerField?.questionType === "multiselect" ||
                triggerField?.questionType === "multi-select"
            );
        });

        const aggregatedFields = [];
        const allSelectedItems = [];

        if (multiselectDeps) {
            multiselectDeps.forEach((dep) => {
                const triggerId = dep.triggerQuestionId || dep.triggerFieldId;
                const triggerField =
                    fields.find((f) => f.fieldId === triggerId) ||
                    questionsList?.find((q) => q.questionId === triggerId);
                const selected =
                    (rowData && rowData[triggerId]) || (answers && answers[triggerId]) || [];

                if (Array.isArray(selected) && selected.length > 0) {
                    aggregatedFields.push({
                        field: triggerField,
                        selectedItems: selected,
                    });
                    allSelectedItems.push(...selected);
                }
            });
        }

        if (aggregatedFields.length > 0) {
            return {
                fields: aggregatedFields,
                allSelectedItems: [...new Set(allSelectedItems)],
            };
        }
        return null;
    };

    if (loading)
        return (
            <div className="p-4 bg-gray-50 border rounded animate-pulse">
                Loading Table...
            </div>
        );
    if (!definition) return null;

    return (
        <div className="space-y-6">
            {isCategorized &&
                categoryFields.map((cf) => (
                    <div
                        key={cf.fieldId}
                        className="mb-4 p-4 bg-blue-50 border-l-4 border-blue-500 rounded"
                    >
                        {cf.guidance && cf.guidance.length > 0 && (
                            <div className="mb-2 p-2 bg-blue-50 rounded border-l-2 border-blue-400">
                                <p className="text-xs font-semibold text-blue-800 mb-1">
                                    💡 Guidance:
                                </p>
                                <ul className="text-xs text-gray-600 space-y-0.5 list-disc list-inside">
                                    {cf.guidance.map((item, idx) => (
                                        <li key={idx} className="leading-relaxed">
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {cf.fieldTitle && cf.fieldTitle.length > 0 && (
                            <p className="block text-sm font-semibold text-gray-800 mb-2 mt-4 border-b  pb-1">
                                {cf.fieldTitle[0]}
                            </p>
                        )}
                        <label className="block font-bold mb-2">
                            {cf.fieldLabel}
                            <RequiredMark show={cf.mandatory || parentMandatory} />
                        </label>
                        <QuestionInput
                            field={cf}
                            value={answers[`${tableId}_${cf.fieldId}`] || []}
                            disabled={readOnly}
                            onChange={(val) => {
                                if (readOnly) return;
                                setAnswers((prev) => {
                                    const prevCatValues = prev[`${tableId}_${cf.fieldId}`] || [];
                                    const nextCatValues = Array.isArray(val) ? val : [];
                                    const deselected = prevCatValues.filter(
                                        (v) => !nextCatValues.includes(v),
                                    );

                                    const nextState = {
                                        ...prev,
                                        [`${tableId}_${cf.fieldId}`]: nextCatValues,
                                    };

                                    if (deselected.length > 0) {
                                        const prevTableData = prev[tableId] || {};
                                        const nextTableData = { ...prevTableData };
                                        deselected.forEach((opt) => {
                                            delete nextTableData[opt];
                                        });
                                        nextState[tableId] = nextTableData;
                                    }

                                    return nextState;
                                });
                            }}
                        />
                    </div>
                ))}
            {selectedCategories.map((catKey) => {
                const tableData = answers[tableId] || {};
                const rows = tableData[catKey] || [];

                let catLabel = catKey;
                for (const cf of categoryFields) {
                    const opt = cf.options?.find((o) => o.value === catKey);
                    if (opt) {
                        catLabel = opt.label;
                        break;
                    }
                }

                return (
                    <div
                        key={catKey}
                        className="border-2 border-gray-200 rounded-xl p-4 bg-white"
                    >
                        {isCategorized && (
                            <h3 className="text-lg font-bold text-teal-700 mb-4 uppercase">
                                {catLabel}
                            </h3>
                        )}
                        {rows.map((row) => {
                            const context = { ...row.values };
                            if (isCategorized) {
                                const activeCategoryFields = categoryFields.filter((cf) => {
                                    const vals = answers[`${tableId}_${cf.fieldId}`] || [];
                                    return vals.includes(catKey);
                                });
                                categoryFields.forEach((cf) => {
                                    if (activeCategoryFields.includes(cf)) {
                                        context[cf.fieldId] = [catKey];
                                    } else {
                                        context[cf.fieldId] = [];
                                    }
                                });
                            }
                            const fieldNumbers = getFieldNumbersForRow(context);
                            return (
                                <div
                                    key={row.rowId}
                                    className="mb-6 p-4 border rounded-lg bg-gray-50 relative"
                                >
                                    <div className="space-y-4">
                                        {(() => {
                                            const renderedFieldIds = new Set();
                                            return fields.map((field, idx) => {
                                                if (
                                                    isCategorized &&
                                                    categoryFields.find(
                                                        (cf) => cf.fieldId === field.fieldId,
                                                    )
                                                )
                                                    return null;
                                                if (renderedFieldIds.has(field.fieldId)) return null;
                                                if (!evalCondition(field, { rowData: context }))
                                                    return null;
                                                const isRepeater =
                                                    field.fieldType === "number" &&
                                                    parseInt(row.values[field.fieldId]) > 0 &&
                                                    fields.some((other) =>
                                                        other.displayCondition?.some(
                                                            (c) =>
                                                                c.triggerQuestionId === field.fieldId ||
                                                                c.triggerFieldId === field.fieldId,
                                                        ),
                                                    );

                                                if (isRepeater) {
                                                    const repeaterCount = parseInt(
                                                        row.values[field.fieldId],
                                                    );

                                                    const loopMemberIds = new Set();
                                                    let previousSize = 0;

                                                    fields.forEach((f) => {
                                                        const dependsOnRoot = f.displayCondition?.some(
                                                            (c) =>
                                                                c.triggerQuestionId === field.fieldId ||
                                                                c.triggerFieldId === field.fieldId,
                                                        );
                                                        if (dependsOnRoot) loopMemberIds.add(f.fieldId);
                                                    });

                                                    while (loopMemberIds.size > previousSize) {
                                                        previousSize = loopMemberIds.size;
                                                        fields.forEach((f) => {
                                                            if (!loopMemberIds.has(f.fieldId)) {
                                                                const dependsOnMember =
                                                                    f.displayCondition?.some((c) => {
                                                                        const tId =
                                                                            c.triggerQuestionId || c.triggerFieldId;
                                                                        return loopMemberIds.has(tId);
                                                                    });
                                                                if (dependsOnMember)
                                                                    loopMemberIds.add(f.fieldId);
                                                            }
                                                        });
                                                    }

                                                    const childFields = fields.filter((f) =>
                                                        loopMemberIds.has(f.fieldId),
                                                    );

                                                    renderedFieldIds.add(field.fieldId);
                                                    childFields.forEach((cf) =>
                                                        renderedFieldIds.add(cf.fieldId),
                                                    );

                                                    return (
                                                        <div key={field.fieldId} className="space-y-4">
                                                            <div key={field.fieldId}>
                                                                {field.fieldTitle &&
                                                                    field.fieldTitle.length > 0 && (
                                                                        <p className="block text-sm font-semibold text-gray-800 mb-2 border-b pb-1">
                                                                            {field.fieldTitle[0]}
                                                                        </p>
                                                                    )}
                                                                {field.guidance &&
                                                                    field.guidance.length > 0 && (
                                                                        <div className="mb-2 p-2 bg-blue-50 rounded border-l-2 border-blue-400">
                                                                            <p className="text-xs font-semibold text-blue-800 mb-1">
                                                                                💡 Guidance:
                                                                            </p>
                                                                            <ul className="text-xs text-gray-600 space-y-0.5 list-disc list-inside">
                                                                                {field.guidance.map((item, gIdx) => (
                                                                                    <li
                                                                                        key={gIdx}
                                                                                        className="leading-relaxed"
                                                                                    >
                                                                                        {item}
                                                                                    </li>
                                                                                ))}
                                                                            </ul>
                                                                        </div>
                                                                    )}
                                                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                                                    <FieldSerialPrefix
                                                                        fieldNumbers={fieldNumbers}
                                                                        fieldId={field.fieldId}
                                                                    />
                                                                    {field.fieldLabel}
                                                                    <RequiredMark show={field.mandatory || parentMandatory} />
                                                                </label>
                                                                <QuestionInput
                                                                    field={field}
                                                                    value={row.values[field.fieldId]}
                                                                    disabled={readOnly}
                                                                    rowData={context}
                                                                    onChange={(val) =>
                                                                        setTableRow(catKey, row.rowId, {
                                                                            ...row.values,
                                                                            [field.fieldId]: val,
                                                                        })
                                                                    }
                                                                />
                                                            </div>

                                                            {Array.from(
                                                                { length: repeaterCount },
                                                                (_, i) => {
                                                                    const loopIndex = i;
                                                                    const loopValues = getLoopContext(
                                                                        loopIndex,
                                                                        row.values,
                                                                    );
                                                                    const loopFieldNumbers =
                                                                        getFieldNumbersForRow(loopValues);

                                                                    return (
                                                                        <div
                                                                            key={i}
                                                                            className="p-4 border-2 border-teal-100 rounded-xl bg-teal-50/10 space-y-4"
                                                                        >
                                                                            <div className="text-teal-600 font-bold uppercase text-xs tracking-wider">
                                                                                Entry {i + 1}
                                                                            </div>
                                                                            {childFields.map((cf) => {
                                                                                if (
                                                                                    !evalCondition(cf, {
                                                                                        rowData: loopValues,
                                                                                    })
                                                                                )
                                                                                    return null;

                                                                                const msDep = getMultiselectDependency(cf, loopValues);
                                                                                if (msDep) {
                                                                                    const { fields: msFields, allSelectedItems } = msDep;
                                                                                    return (
                                                                                        <div key={cf.fieldId} className="space-y-3">
                                                                                            {cf.fieldTitle && cf.fieldTitle.length > 0 && (
                                                                                                <p className="block text-sm font-semibold text-gray-800 border-b pb-1">
                                                                                                    {cf.fieldTitle[0]}
                                                                                                </p>
                                                                                            )}
                                                                                            {cf.guidance && cf.guidance.length > 0 && (
                                                                                                <div className="mb-2 p-2 bg-blue-50 rounded border-l-2 border-blue-400">
                                                                                                    <p className="text-xs font-semibold text-blue-800 mb-1">💡 Guidance:</p>
                                                                                                    <ul className="text-xs text-gray-600 space-y-0.5 list-disc list-inside">
                                                                                                        {cf.guidance.map((item, gIdx) => (
                                                                                                            <li key={gIdx} className="leading-relaxed">{item}</li>
                                                                                                        ))}
                                                                                                    </ul>
                                                                                                </div>
                                                                                            )}
                                                                                            <label className="block text-sm font-semibold text-gray-700 mb-1">
                                                                                                <FieldSerialPrefix fieldNumbers={loopFieldNumbers} fieldId={cf.fieldId} />
                                                                                                {cf.fieldLabel}
                                                                                            </label>
                                                                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                                                                                {allSelectedItems.map((itemVal) => {
                                                                                                    const sourceMs = msFields.find((mf) => mf.selectedItems.includes(itemVal));
                                                                                                    const opt = sourceMs?.field?.options?.find((o) => o.value === itemVal);
                                                                                                    const optLabel = opt?.label || itemVal;
                                                                                                    const valueKey = `${cf.fieldId}_loop_${loopIndex}_${itemVal}`;
                                                                                                    return (
                                                                                                        <div key={itemVal} className="p-3 bg-white border border-gray-200 rounded-lg shadow-2xs">
                                                                                                            <label className="block text-xs font-semibold text-gray-700 mb-1">
                                                                                                                {optLabel}
                                                                                                                <RequiredMark show={cf.mandatory || parentMandatory} />
                                                                                                            </label>
                                                                                                            <QuestionInput
                                                                                                                field={cf}
                                                                                                                value={row.values[valueKey]}
                                                                                                                disabled={readOnly}
                                                                                                                onChange={(val) =>
                                                                                                                    setTableRow(catKey, row.rowId, {
                                                                                                                        ...row.values,
                                                                                                                        [valueKey]: val,
                                                                                                                    })
                                                                                                                }
                                                                                                            />
                                                                                                        </div>
                                                                                                    );
                                                                                                })}
                                                                                            </div>
                                                                                        </div>
                                                                                    );
                                                                                }

                                                                                const valueKey = `${cf.fieldId}_loop_${loopIndex}`;
                                                                                const currentValue =
                                                                                    row.values[valueKey];

                                                                                return (
                                                                                    <div key={cf.fieldId}>
                                                                                        {cf.fieldTitle &&
                                                                                            cf.fieldTitle.length > 0 && (
                                                                                                <p className="block text-sm font-semibold text-gray-800 mb-2 border-b pb-1">
                                                                                                    {cf.fieldTitle[0]}
                                                                                                </p>
                                                                                            )}
                                                                                        {cf.guidance &&
                                                                                            cf.guidance.length > 0 && (
                                                                                                <div className="mb-2 p-2 bg-blue-50 rounded border-l-2 border-blue-400">
                                                                                                    <p className="text-xs font-semibold text-blue-800 mb-1">
                                                                                                        💡 Guidance:
                                                                                                    </p>
                                                                                                    <ul className="text-xs text-gray-600 space-y-0.5 list-disc list-inside">
                                                                                                        {cf.guidance.map(
                                                                                                            (
                                                                                                                item,
                                                                                                                gIdx,
                                                                                                            ) => (
                                                                                                                <li
                                                                                                                    key={
                                                                                                                        gIdx
                                                                                                                    }
                                                                                                                    className="leading-relaxed"
                                                                                                                >
                                                                                                                    {
                                                                                                                        item
                                                                                                                    }
                                                                                                                </li>
                                                                                                            ),
                                                                                                        )}
                                                                                                    </ul>
                                                                                                </div>
                                                                                            )}
                                                                                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                                                                                            <FieldSerialPrefix
                                                                                                fieldNumbers={
                                                                                                    loopFieldNumbers
                                                                                                }
                                                                                                fieldId={cf.fieldId}
                                                                                            />
                                                                                            {cf.fieldLabel}
                                                                                            <RequiredMark show={cf.mandatory || parentMandatory} />
                                                                                        </label>

                                                                                        <QuestionInput
                                                                                            field={cf}
                                                                                            value={currentValue}
                                                                                            disabled={readOnly}
                                                                                            rowData={loopValues}
                                                                                            onChange={(val) =>
                                                                                                setTableRow(
                                                                                                    catKey,
                                                                                                    row.rowId,
                                                                                                    {
                                                                                                        ...row.values,
                                                                                                        [valueKey]:
                                                                                                            val,
                                                                                                    },
                                                                                                )
                                                                                            }
                                                                                        />
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    );
                                                                },
                                                            )}
                                                        </div>
                                                    );
                                                }

                                                const msDep = getMultiselectDependency(field, context);
                                                if (msDep) {
                                                    const { fields: msFields, allSelectedItems } = msDep;
                                                    renderedFieldIds.add(field.fieldId);
                                                    return (
                                                        <div key={field.fieldId} className="space-y-3">
                                                            {field.fieldTitle && field.fieldTitle.length > 0 && (
                                                                <p className="block text-sm font-semibold text-gray-800 border-b pb-1">
                                                                    {field.fieldTitle[0]}
                                                                </p>
                                                            )}
                                                            {field.guidance && field.guidance.length > 0 && (
                                                                <div className="mb-2 p-2 bg-blue-50 rounded border-l-2 border-blue-400">
                                                                    <p className="text-xs font-semibold text-blue-800 mb-1">💡 Guidance:</p>
                                                                    <ul className="text-xs text-gray-600 space-y-0.5 list-disc list-inside">
                                                                        {field.guidance.map((item, gIdx) => (
                                                                            <li key={gIdx} className="leading-relaxed">{item}</li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            )}
                                                            <label className="block text-sm font-semibold text-gray-700 mb-1">
                                                                <FieldSerialPrefix fieldNumbers={fieldNumbers} fieldId={field.fieldId} />
                                                                {field.fieldLabel}
                                                            </label>
                                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                                                {allSelectedItems.map((itemVal) => {
                                                                    const sourceMs = msFields.find((mf) => mf.selectedItems.includes(itemVal));
                                                                    const opt = sourceMs?.field?.options?.find((o) => o.value === itemVal);
                                                                    const optLabel = opt?.label || itemVal;
                                                                    const valueKey = `${field.fieldId}_${itemVal}`;
                                                                    return (
                                                                        <div key={itemVal} className="p-3 bg-white border border-gray-200 rounded-lg shadow-2xs">
                                                                            <label className="block text-xs font-semibold text-gray-700 mb-1">
                                                                                {optLabel}
                                                                                <RequiredMark show={field.mandatory || parentMandatory} />
                                                                            </label>
                                                                            <QuestionInput
                                                                                field={field}
                                                                                value={row.values[valueKey]}
                                                                                disabled={readOnly}
                                                                                onChange={(val) =>
                                                                                    setTableRow(catKey, row.rowId, {
                                                                                        ...row.values,
                                                                                        [valueKey]: val,
                                                                                    })
                                                                                }
                                                                            />
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    );
                                                }

                                                renderedFieldIds.add(field.fieldId);
                                                return (
                                                    <div key={field.fieldId}>
                                                        {field.fieldTitle &&
                                                            field.fieldTitle.length > 0 && (
                                                                <p className="block text-sm font-semibold text-gray-800 mb-2 border-b pb-1">
                                                                    {field.fieldTitle[0]}
                                                                </p>
                                                            )}
                                                        {field.guidance && field.guidance.length > 0 && (
                                                            <div className="mb-2 p-2 bg-blue-50 rounded border-l-2 border-blue-400">
                                                                <p className="text-xs font-semibold text-blue-800 mb-1">
                                                                    💡 Guidance:
                                                                </p>
                                                                <ul className="text-xs text-gray-600 space-y-0.5 list-disc list-inside">
                                                                    {field.guidance.map((item, gIdx) => (
                                                                        <li key={gIdx} className="leading-relaxed">
                                                                            {item}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        )}
                                                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                                                            <FieldSerialPrefix
                                                                fieldNumbers={fieldNumbers}
                                                                fieldId={field.fieldId}
                                                            />
                                                            {field.fieldLabel}
                                                            <RequiredMark show={field.mandatory || parentMandatory} />
                                                        </label>
                                                        <QuestionInput
                                                            field={field}
                                                            value={row.values[field.fieldId]}
                                                            disabled={readOnly}
                                                            rowData={context}
                                                            onChange={(val) =>
                                                                setTableRow(catKey, row.rowId, {
                                                                    ...row.values,
                                                                    [field.fieldId]: val,
                                                                })
                                                            }
                                                        />
                                                    </div>
                                                );
                                            });
                                        })()}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                );
            })}
        </div>
    );
};

const DEFAULT_PREFILLED_ANSWERS = [];
const DEFAULT_FLAT_ANSWERS = {};

export const QuestionFormScope1234 = ({
    questions: questionsProp,
    sectionId,
    subSectionId,
    prefilledAnswers = DEFAULT_PREFILLED_ANSWERS,
    flatAnswers = DEFAULT_FLAT_ANSWERS,
    answers: controlledAnswers,
    setAnswers: controlledSetAnswers,
    onPrevious,
    onNext,
    canGoPrevious = false,
    canGoNext = false,
}) => {
    const [localAnswers, setLocalAnswers] = useState({});
    const [tableDefs, setTableDefs] = useState({});
    const [optionsCache, setOptionsCache] = useState({});
    const [isLoadingAnswers, setIsLoadingAnswers] = useState(false);

    const answers = controlledAnswers ?? localAnswers;
    const setAnswers = controlledSetAnswers ?? setLocalAnswers;

    const reconstructAnswersState = useCallback((apiAnswers) => {
        const initialState = {};

        apiAnswers.forEach(ans => {
            const { questionId, questionType, value, tableRows, optionValue, tripIndex } = ans;

            if (questionType === "table" && tableRows) {
                const tableData = {};
                tableRows.forEach(row => {
                    const flattenedValues = { ...row.values };
                    Object.keys(row.values).forEach(fieldId => {
                        const val = row.values[fieldId];
                        if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'object' && val[0].hasOwnProperty('key')) {
                            val.forEach(item => {
                                flattenedValues[`${fieldId}_${item.key}`] = item.value;
                            });
                        }
                    });

                    const processedRow = { ...row, values: flattenedValues };
                    const itemType = Array.isArray(processedRow.values.item_type) ? processedRow.values.item_type[0] : "default";
                    if (!tableData[itemType]) tableData[itemType] = [];
                    tableData[itemType].push(processedRow);
                });

                let key = questionId;
                if (tripIndex !== null) key += `_trip_${tripIndex}`;
                if (optionValue) key += `_${optionValue}`;
                initialState[key] = tableData;

                if (questionId === "scope3_categories_capital_goods_table") {
                    initialState["scope3_categories_non_capital_goods_table"] = tableData;
                } else if (questionId === "scope3_categories_non_capital_goods_table") {
                    initialState["scope3_categories_capital_goods_table"] = tableData;
                }
            }
            else if (subSectionId === "business_travel" && (questionId.startsWith("number_of_trip_"))) {
                const isIndividual = questionId.includes("_individual");
                const branchId = isIndividual ? "individual_business_travel" : "group_business_travel";
                const detailsKey = `${questionId}_details`;
                const details = ans[detailsKey];

                initialState[questionId] = value;

                if (Array.isArray(details)) {
                    details.forEach((tripObj, idx) => {
                        const tripLabel = `${questionId}_${idx + 1}`;
                        const tripAnswers = tripObj[tripLabel];
                        if (Array.isArray(tripAnswers)) {
                            tripAnswers.forEach(childAns => {
                                const childQid = childAns.questionId || childAns.key;
                                if (!childQid) return;

                                const suffix = `_trip_${idx}`;
                                const stateKey = `${childQid}${suffix}`;

                                if (childAns.tableRows) {
                                    const tableData = {};
                                    childAns.tableRows.forEach(row => {
                                        const flattenedValues = { ...row.values };
                                        Object.keys(row.values).forEach(fieldId => {
                                            const val = row.values[fieldId];
                                            if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'object' && val[0].hasOwnProperty('key')) {
                                                val.forEach(item => {
                                                    flattenedValues[`${fieldId}_${item.key}`] = item.value;
                                                });
                                            }
                                        });
                                        const processedRow = { ...row, values: flattenedValues };
                                        const itemType = Array.isArray(processedRow.values.item_type) ? processedRow.values.item_type[0] : "default";
                                        if (!tableData[itemType]) tableData[itemType] = [];
                                        tableData[itemType].push(processedRow);
                                    });
                                    initialState[stateKey] = tableData;
                                } else {
                                    initialState[stateKey] = childAns.value;
                                }
                            });
                        }
                    });
                }
            }
            else if (Array.isArray(value) && value.length > 0 && value[0].key) {
                value.forEach(item => {
                    initialState[`${questionId}_${item.key}`] = item.value;
                });
            }
            else if (optionValue) {
                let key = `${questionId}`;
                if (tripIndex !== null) key += `_trip_${tripIndex}`;
                key += `_${optionValue}`;
                initialState[key] = value;
            }
            else {
                const isMultiselect = questionType === "multiselect";
                if (isMultiselect) {
                    const current = initialState[questionId]
                        ? (Array.isArray(initialState[questionId]) ? initialState[questionId] : [initialState[questionId]])
                        : [];
                    const next = Array.isArray(value) ? value : (value !== undefined ? [value] : []);
                    initialState[questionId] = Array.from(new Set([...current, ...next]));
                } else {
                    if (value !== undefined && value !== "" && !Array.isArray(value)) {
                        if (initialState[questionId] === undefined || initialState[questionId] === "" || initialState[questionId] === null || Array.isArray(initialState[questionId])) {
                            initialState[questionId] = value;
                        }
                    }
                }
            }
        });

        return initialState;
    }, [subSectionId]);

    useEffect(() => {
        if (flatAnswers && Object.keys(flatAnswers).length > 0) {
            setAnswers(flatAnswers);
        } else if (prefilledAnswers && Array.isArray(prefilledAnswers)) {
            setIsLoadingAnswers(true);
            try {
                const reconstructed = reconstructAnswersState(prefilledAnswers);
                setAnswers(reconstructed);
            } finally {
                setIsLoadingAnswers(false);
            }
        }
    }, [prefilledAnswers, flatAnswers, reconstructAnswersState]);

    const questionsList = useMemo(() => {
        if (!questionsProp) return [];
        let flat = [];

        if (Array.isArray(questionsProp)) {
            questionsProp.forEach((item) => {
                if (item?.questions && Array.isArray(item.questions)) {
                    flat.push(...item.questions);
                } else if (item?.questionId) {
                    flat.push(item);
                }
            });
        }

        const uniqueMap = new Map();
        flat.forEach((q) => {
            const existing = uniqueMap.get(q.questionId);
            if (!existing) {
                uniqueMap.set(q.questionId, q);
            } else {
                if (
                    (!existing.displayCondition ||
                        existing.displayCondition.length === 0) &&
                    q.displayCondition &&
                    q.displayCondition.length > 0
                ) {
                    uniqueMap.set(q.questionId, q);
                }
            }
        });

        return Array.from(uniqueMap.values());
    }, [questionsProp]);

    const questionTree = useMemo(
        () => buildQuestionTree(questionsList),
        [questionsList],
    );

    const SUFFIX_SEP = ":::";

    const getMultiselectDependency = (
        field,
        currentAnswers,
        tripIndex = null,
        optionSuffix = null,
        extraFields = [],
    ) => {
        if (!field.displayCondition) return null;

        const multiselectDep = field.displayCondition.find((cond) => {
            if (cond.operator !== "exists") return false;
            let triggerField = questionsList.find(
                (q) => q.questionId === cond.triggerQuestionId,
            );

            if (!triggerField && extraFields && extraFields.length > 0) {
                triggerField = extraFields.find(
                    (q) =>
                        q.fieldId === cond.triggerQuestionId ||
                        q.questionId === cond.triggerQuestionId,
                );
            }

            return (
                triggerField?.questionType === "multiselect" ||
                triggerField?.fieldType === "multiselect"
            );
        });

        if (!multiselectDep) return null;

        const multiselectFieldId = multiselectDep.triggerQuestionId;

        let selectedItems;
        if (currentAnswers[multiselectFieldId]) {
            selectedItems = currentAnswers[multiselectFieldId];
        }

        if (selectedItems === undefined) {
            let s = optionSuffix;
            while (selectedItems === undefined) {
                if (tripIndex !== null && s)
                    selectedItems =
                        currentAnswers[`${multiselectFieldId}_trip_${tripIndex}_${s}`];
                if (selectedItems === undefined && s)
                    selectedItems = currentAnswers[`${multiselectFieldId}_${s}`];
                if (selectedItems === undefined && tripIndex !== null)
                    selectedItems =
                        currentAnswers[`${multiselectFieldId}_trip_${tripIndex}`];
                if (selectedItems === undefined)
                    selectedItems = currentAnswers[multiselectFieldId];

                if (selectedItems !== undefined || !s || !s.includes(SUFFIX_SEP)) break;
                s = s.substring(0, s.lastIndexOf(SUFFIX_SEP));
            }
        }

        if (!Array.isArray(selectedItems) || selectedItems.length === 0) {
            return null;
        }

        return multiselectFieldId;
    };

    const evalCondition = (cond, context = {}) => {
        if (!cond) return true;

        if (
            cond.questionId === "scope3_categories_non_capital_goods_table" ||
            cond._id === "scope3_categories_non_capital_goods_table"
        ) {
            const currentAnswers = context.currentAnswers || answers;

            const getValAnySuffix = (triggerId) => {
                if (currentAnswers[triggerId] !== undefined)
                    return currentAnswers[triggerId];
                const key = Object.keys(currentAnswers).find(
                    (k) =>
                        k === triggerId ||
                        k.startsWith(`${triggerId}_`) ||
                        k.startsWith(`${triggerId}${SUFFIX_SEP}`),
                );
                return key ? currentAnswers[key] : undefined;
            };

            const scope3First = getValAnySuffix("scope3_first");
            const scope3Categories = getValAnySuffix("scope3_categories");

            const firstExists =
                scope3First !== undefined && scope3First !== null && scope3First !== "";
            if (!firstExists) return false;

            const categoriesList = Array.isArray(scope3Categories)
                ? scope3Categories
                : scope3Categories
                    ? [scope3Categories]
                    : [];
            return !categoriesList.includes("capital_goods");
        }

        const triggerId = cond.triggerQuestionId || cond.triggerFieldId;
        const currentAnswers = context.currentAnswers || answers;

        if (context.repeatingParentId && triggerId === context.repeatingParentId)
            return true;

        if (cond.displayCondition) {
            const conditions = cond.displayCondition;
            if (!conditions || conditions.length === 0) return true;

            const logicalOp = (cond.logicalOperator || "AND").toUpperCase();
            const check = (c) => evalCondition(c, context);

            if (logicalOp === "OR") return conditions.some(check);
            return conditions.every(check);
        }

        if (Array.isArray(cond)) {
            return cond.every((c) => evalCondition(c, context));
        }

        if (!cond.triggerQuestionId && !cond.triggerFieldId) return true;
        if (!cond.operator) return true;

        const triggerQuestionId = cond.triggerQuestionId || cond.triggerFieldId;
        const { operator, value: expected } = cond;
        let actual;

        if (context.rowData && context.rowData.hasOwnProperty(triggerQuestionId)) {
            actual = context.rowData[triggerQuestionId];
        } else {
            const tripIdx = context.tripIndex;
            let s = context.optionSuffix;

            while (actual === undefined) {
                if (tripIdx !== null && s) {
                    actual = currentAnswers[`${triggerQuestionId}_trip_${tripIdx}_${s}`];
                }
                if (actual === undefined && s) {
                    actual = currentAnswers[`${triggerQuestionId}_${s}`];
                }
                if (actual === undefined && tripIdx !== null) {
                    actual = currentAnswers[`${triggerQuestionId}_trip_${tripIdx}`];
                }
                if (actual === undefined) {
                    actual = currentAnswers[triggerQuestionId];
                }

                if (actual !== undefined || !s || !s.includes(SUFFIX_SEP)) break;
                s = s.substring(0, s.lastIndexOf(SUFFIX_SEP));
            }
        }

        if (operator === "exists" || operator === "exist") {
            let exists;
            if (Array.isArray(actual)) {
                exists = actual.length > 0;
            } else if (
                typeof actual === "number" ||
                (!isNaN(actual) && actual !== "")
            ) {
                exists = parseFloat(actual) > 0;
            } else {
                exists = actual !== undefined && actual !== null && actual !== "";
            }

            const triggerId = String(cond.triggerQuestionId || cond.triggerFieldId || "").toLowerCase();
            if (!exists && (triggerId.includes("country") || triggerId.includes("state"))) {
                return true;
            }
          
            return expected !== undefined ? (expected ? exists : !exists) : exists;
        }

        if (Array.isArray(actual)) {
            if (operator === "eq") return actual.includes(expected);
            if (operator === "ne") {
                if (actual.length === 0) return false;
                return actual.some((val) => val !== expected);
            }
        }

        const actualVal = actual === undefined || actual === null ? "" : actual;
        if (operator === "eq") return actualVal == expected;
        if (operator === "ne") {
            if (
                actualVal === "" ||
                (Array.isArray(actualVal) && actualVal.length === 0)
            )
                return false;
            return actualVal != expected;
        }

        return true;
    };

    const buildQuestionInstanceKey = (
        questionId,
        tripIndex = null,
        optionSuffix = null,
    ) => {
        let qid = questionId;
        if (tripIndex !== null) qid += `_trip_${tripIndex}`;
        if (optionSuffix) qid += `_${optionSuffix}`;
        return qid;
    };

    const collectQuestionDescendantIds = (parentId, collected = new Set()) => {
        questionsList.forEach((child) => {
            if (
                child.displayCondition?.some(
                    (condition) => condition.triggerQuestionId === parentId,
                )
            ) {
                if (!collected.has(child.questionId)) {
                    collected.add(child.questionId);
                    collectQuestionDescendantIds(child.questionId, collected);
                }
            }
        });

        return collected;
    };

    const getVisibleChildQuestions = (
        parentId,
        context = {},
        sourceAnswers = answers,
    ) => {
        return questionsList.filter((child) => {
            if (
                child.questionId === "scope3_categories_non_capital_goods_table" &&
                parentId === "scope3_first"
            ) {
                return evalCondition(child, {
                    ...context,
                    currentAnswers: sourceAnswers,
                });
            }

            if (
                child.questionId === "scope3_categories_non_capital_goods_table" &&
                parentId === "scope3_categories"
            ) {
                return false;
            }

            if (!child.displayCondition) return false;

            const parentConditions = child.displayCondition.filter(
                (condition) => condition.triggerQuestionId === parentId,
            );

            if (parentConditions.length === 0) return false;

            const logicalOp = (child.logicalOperator || "AND").toUpperCase();
            const isSatisfied = (condition) => {
                if (
                    context.repeatingParentId &&
                    condition.triggerQuestionId === parentId
                ) {
                    return true;
                }

                return evalCondition(condition, {
                    ...context,
                    currentAnswers: sourceAnswers,
                });
            };

            if (logicalOp === "OR") {
                return parentConditions.some(isSatisfied);
            }

            return parentConditions.some(isSatisfied);
        });
    };

    const collectVisibleQuestionKeys = (
        parentId,
        context = {},
        sourceAnswers = answers,
        visibleKeys = new Set(),
    ) => {
        const visibleChildren = getVisibleChildQuestions(
            parentId,
            context,
            sourceAnswers,
        );

        visibleChildren.forEach((child) => {
            let childSuffix = context.optionSuffix;

            if (child.displayCondition) {
                const specificCondition = child.displayCondition.find(
                    (condition) =>
                        condition.triggerQuestionId === parentId &&
                        condition.operator === "eq" &&
                        typeof condition.value === "string",
                );

                if (specificCondition) {
                    childSuffix = context.optionSuffix
                        ? `${context.optionSuffix}${SUFFIX_SEP}${specificCondition.value}`
                        : specificCondition.value;
                }
            }

            const multiselectTriggerId = getMultiselectDependency(
                child,
                sourceAnswers,
                context.tripIndex ?? null,
                childSuffix,
            );

            if (multiselectTriggerId) {
                let selectedItems = sourceAnswers[multiselectTriggerId];

                if (!selectedItems) {
                    let suffixCursor = childSuffix;

                    while (!selectedItems) {
                        if (
                            context.tripIndex !== null &&
                            context.tripIndex !== undefined &&
                            suffixCursor
                        ) {
                            selectedItems =
                                sourceAnswers[
                                `${multiselectTriggerId}_trip_${context.tripIndex}_${suffixCursor}`
                                ];
                        }
                        if (!selectedItems && suffixCursor) {
                            selectedItems =
                                sourceAnswers[`${multiselectTriggerId}_${suffixCursor}`];
                        }
                        if (
                            !selectedItems &&
                            context.tripIndex !== null &&
                            context.tripIndex !== undefined
                        ) {
                            selectedItems =
                                sourceAnswers[
                                `${multiselectTriggerId}_trip_${context.tripIndex}`
                                ];
                        }
                        if (!selectedItems) {
                            selectedItems = sourceAnswers[multiselectTriggerId];
                        }

                        if (
                            selectedItems ||
                            !suffixCursor ||
                            !suffixCursor.includes(SUFFIX_SEP)
                        ) {
                            break;
                        }

                        suffixCursor = suffixCursor.substring(
                            0,
                            suffixCursor.lastIndexOf(SUFFIX_SEP),
                        );
                    }
                }

                (selectedItems || [])
                    .filter((item) => item !== "")
                    .forEach((itemValue) => {
                        const itemSuffix = childSuffix
                            ? `${childSuffix}${SUFFIX_SEP}${itemValue}`
                            : itemValue;

                        const questionKey = buildQuestionInstanceKey(
                            child.questionId,
                            context.tripIndex ?? null,
                            itemSuffix,
                        );

                        visibleKeys.add(questionKey);

                        const currentValue = sourceAnswers[questionKey];
                        const hasChildDependents = questionsList.some((question) =>
                            question.displayCondition?.some(
                                (condition) => condition.triggerQuestionId === child.questionId,
                            ),
                        );

                        if (
                            (child.questionType === "number" ||
                                child.questionId === "number_of_trip_select") &&
                            parseInt(currentValue) > 0 &&
                            hasChildDependents
                        ) {
                            Array.from(
                                { length: Math.min(parseInt(currentValue), 100) },
                                (_, index) => index,
                            ).forEach((loopIndex) => {
                                collectVisibleQuestionKeys(
                                    child.questionId,
                                    {
                                        tripIndex: loopIndex,
                                        optionSuffix: itemSuffix,
                                        repeatingParentId: child.questionId,
                                    },
                                    sourceAnswers,
                                    visibleKeys,
                                );
                            });
                        } else {
                            collectVisibleQuestionKeys(
                                child.questionId,
                                {
                                    tripIndex: context.tripIndex ?? null,
                                    optionSuffix: itemSuffix,
                                    repeatingParentId: context.repeatingParentId || null,
                                },
                                sourceAnswers,
                                visibleKeys,
                            );
                        }
                    });

                return;
            }

            const questionKey = buildQuestionInstanceKey(
                child.questionId,
                context.tripIndex ?? null,
                childSuffix,
            );

            visibleKeys.add(questionKey);

            const currentValue = sourceAnswers[questionKey];
            const hasChildDependents = questionsList.some((question) =>
                question.displayCondition?.some(
                    (condition) => condition.triggerQuestionId === child.questionId,
                ),
            );

            if (
                (child.questionType === "number" ||
                    child.questionId === "number_of_trip_select") &&
                parseInt(currentValue) > 0 &&
                hasChildDependents
            ) {
                Array.from(
                    { length: Math.min(parseInt(currentValue), 100) },
                    (_, index) => index,
                ).forEach((loopIndex) => {
                    collectVisibleQuestionKeys(
                        child.questionId,
                        {
                            tripIndex: loopIndex,
                            optionSuffix: childSuffix,
                            repeatingParentId: child.questionId,
                        },
                        sourceAnswers,
                        visibleKeys,
                    );
                });
            } else {
                collectVisibleQuestionKeys(
                    child.questionId,
                    {
                        tripIndex: context.tripIndex ?? null,
                        optionSuffix: childSuffix,
                        repeatingParentId: context.repeatingParentId || null,
                    },
                    sourceAnswers,
                    visibleKeys,
                );
            }
        });

        return visibleKeys;
    };

    const clearHiddenDescendantAnswers = (
        parentId,
        nextAnswers,
        context = {},
    ) => {
        const descendantQuestionIds = collectQuestionDescendantIds(parentId);
        if (descendantQuestionIds.size === 0) return nextAnswers;

        const visibleKeys = collectVisibleQuestionKeys(
            parentId,
            context,
            nextAnswers,
            new Set(),
        );

        const updatedAnswers = { ...nextAnswers };

        Object.keys(updatedAnswers).forEach((answerKey) => {
            const belongsToDescendant = Array.from(descendantQuestionIds).some(
                (questionId) =>
                    answerKey === questionId || answerKey.startsWith(`${questionId}_`),
            );

            if (!belongsToDescendant) return;

            if (context.optionSuffix) {
                const escapedSuffix = context.optionSuffix.replace(
                    /[.*+?^${}()|[\]\\]/g,
                    "\\$&",
                );
                const suffixPattern = `_${escapedSuffix}(?:$|${SUFFIX_SEP})`;
                const regex = new RegExp(suffixPattern);
                if (!regex.test(answerKey)) {
                    return;
                }
            }

            if (
                context.tripIndex !== null &&
                context.tripIndex !== undefined &&
                context.repeatingParentId !== parentId
            ) {
                const tripPattern = `_trip_${context.tripIndex}(?:_|$)`;
                const regex = new RegExp(tripPattern);
                if (!regex.test(answerKey)) {
                    return;
                }
            }

            const stillVisible = Array.from(visibleKeys).some(
                (visibleKey) =>
                    answerKey === visibleKey ||
                    answerKey.startsWith(`${visibleKey}_`) ||
                    answerKey.startsWith(`${visibleKey}${SUFFIX_SEP}`),
            );

            if (!stillVisible) {
                delete updatedAnswers[answerKey];
            }
        });

        return updatedAnswers;
    };

    const isChildVisible = (
        child,
        parentId,
        tripIndex,
        optionSuffix,
        repeatingParentId,
    ) => {
        if (
            (child.questionId === "scope3_categories_capital_goods_table" ||
                child.questionId === "scope3_categories_non_capital_goods_table") &&
            parentId === "scope3_first"
        ) {
            return evalCondition(child, {
                tripIndex,
                optionSuffix,
                repeatingParentId,
            });
        }

        if (!child.displayCondition) return false;

        const parentConditions = child.displayCondition.filter(
            (c) => c.triggerQuestionId === parentId,
        );
        if (parentConditions.length === 0) return false;

        const logicalOp = (child.logicalOperator || "AND").toUpperCase();
        const isSatisfied = (c) => {
            if (repeatingParentId && c.triggerQuestionId === parentId) return true;
            return evalCondition(c, {
                tripIndex,
                optionSuffix,
                repeatingParentId,
            });
        };

        if (logicalOp === "OR") {
            return parentConditions.some(isSatisfied);
        }

        return parentConditions.some(isSatisfied);
    };

    const renderChildren = (
        parentId,
        parentQuestionNumber,
        depth,
        tripIndex,
        optionSuffix = null,
        repeatingParentId = null,
        isParentMandatory = false,
    ) => {
        const candidates =
            questionTree.childrenByParent.get(parentId) ||
            questionsList.filter((child) =>
                child.displayCondition?.some((c) => c.triggerQuestionId === parentId),
            );

        const visibleChildren = sortByOrderIndex(
            candidates.filter((child) =>
                isChildVisible(
                    child,
                    parentId,
                    tripIndex,
                    optionSuffix,
                    repeatingParentId,
                ),
            ),
            questionTree.orderIndex,
            (q) => q.questionId,
        );

        let siblingIndex = 0;

        return visibleChildren.map((child) => {
            let childQuestionNumber = "";
            if (isNumberedType(child.questionType)) {
                siblingIndex += 1;
                childQuestionNumber = formatSerialNumber(
                    parentQuestionNumber,
                    siblingIndex,
                );
            }

            let childSuffix = optionSuffix;

            if (child.displayCondition) {
                const specificCond = child.displayCondition.find(
                    (c) =>
                        c.triggerQuestionId === parentId &&
                        c.operator === "eq" &&
                        typeof c.value === "string",
                );
                if (specificCond) {
                    childSuffix = optionSuffix
                        ? `${optionSuffix}${SUFFIX_SEP}${specificCond.value}`
                        : specificCond.value;
                }
            }

            return renderQuestion(
                child,
                depth,
                tripIndex,
                childSuffix,
                repeatingParentId,
                childQuestionNumber,
                isParentMandatory,
            );
        });
    };

    const renderQuestionInstance = (
        question,
        depth,
        tripIndex,
        optionSuffix = null,
        repeatingParentId = null,
        questionNumber = "",
        isParentMandatory = false,
    ) => {
        const originalQid = question.questionId;
        const effectiveMandatory = !!(question.mandatory || isParentMandatory);

        let optionLabel = optionSuffix;
        let isMultiselectDependent = false;

        if (optionSuffix) {
            const displaySuffix = optionSuffix.includes(SUFFIX_SEP)
                ? optionSuffix.substring(
                    optionSuffix.lastIndexOf(SUFFIX_SEP) + SUFFIX_SEP.length,
                )
                : optionSuffix;
            optionLabel = displaySuffix;

            if (question.displayCondition) {
                const multiselectExistsDep = question.displayCondition.find((cond) => {
                    if (cond.operator !== "exists") return false;
                    const triggerField = questionsList.find(
                        (q) => q.questionId === cond.triggerQuestionId,
                    );
                    return triggerField?.questionType === "multiselect";
                });

                if (multiselectExistsDep) {
                    isMultiselectDependent = true;
                    const triggerId = multiselectExistsDep.triggerQuestionId;
                    const triggerQuestion = questionsList.find(
                        (q) => q.questionId === triggerId,
                    );
                    let foundOption = null;

                    if (
                        triggerQuestion?.options &&
                        Array.isArray(triggerQuestion.options)
                    ) {
                        foundOption = triggerQuestion.options.find(
                            (opt) => opt.value === displaySuffix,
                        );
                    }

                    if (!foundOption) {
                        const cachedOptions = optionsCache[triggerId];
                        if (cachedOptions && Array.isArray(cachedOptions)) {
                            foundOption = cachedOptions.find(
                                (opt) => opt.value === displaySuffix,
                            );
                        }
                    }

                    if (!foundOption && tableDefs[triggerId]) {
                        const tableDef = tableDefs[triggerId];
                        if (tableDef.fields) {
                            const categoryField = tableDef.fields.find(
                                (f) =>
                                    f.fieldType === "multiselect" &&
                                    (!f.displayCondition || f.displayCondition.length === 0),
                            );
                            if (categoryField?.options) {
                                foundOption = categoryField.options.find(
                                    (opt) => opt.value === displaySuffix,
                                );
                            }
                        }
                    }

                    if (foundOption) {
                        optionLabel = foundOption.label;
                    }
                }
            }
        }

        let qid = originalQid;
        if (tripIndex !== null) qid += `_trip_${tripIndex}`;
        if (optionSuffix) qid += `_${optionSuffix}`;

        const currentValue = answers[qid];
        const hasConditionalChildren = questionsList.some((child) =>
            child.displayCondition?.some(
                (condition) => condition.triggerQuestionId === originalQid,
            ),
        );

        const handleQuestionValueChange = (val) => {
            setAnswers((prev) => {
                const nextAnswers = {
                    ...prev,
                    [qid]: val,
                };

                if (!hasConditionalChildren) {
                    return nextAnswers;
                }

                return clearHiddenDescendantAnswers(originalQid, nextAnswers, {
                    tripIndex,
                    optionSuffix,
                    repeatingParentId,
                });
            });
        };

        const handleQuestionBlur = () => {
            if (
                !hasConditionalChildren ||
                (question.questionType !== "number" &&
                    question.questionType !== "percentage")
            ) {
                return;
            }

            setAnswers((prev) =>
                clearHiddenDescendantAnswers(originalQid, prev, {
                    tripIndex,
                    optionSuffix,
                    repeatingParentId,
                }),
            );
        };

        const displayNumber = formatSerialDisplay(questionNumber);

        return (
            <div
                key={qid}
                className={`bg-white rounded-lg shadow-sm border border-gray-200 p-5 ${depth > 0 ? " mt-3 border-l-4 border-l-teal-500" : "mb-4"}`}
            >
                <label className="block text-base font-semibold text-gray-800 mb-3">
                    {question.questionTitle && question.questionTitle.length > 0 && (
                        <div className="text-teal-700 font-semibold mb-2 border-b pb-1">
                            {question.questionTitle[0]}
                        </div>
                    )}
                    {question.guidance && question.guidance.length > 0 && (
                        <div className="mb-3 p-3 bg-blue-50 rounded border-l-4 border-blue-400">
                            <p className="text-xs font-semibold text-blue-800 mb-2">
                                💡 Guidance:
                            </p>
                            <ul className="text-xs text-gray-700 space-y-1 list-disc list-inside">
                                {question.guidance.map((item, idx) => (
                                    <li key={idx} className="leading-relaxed">
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    <span className="text-teal-700 mr-2">{displayNumber}</span>
                    {question.questionText}
                    {optionSuffix && isMultiselectDependent && (
                        <span className="ml-2 px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-medium">
                            {optionLabel}
                        </span>
                    )}
                    <RequiredMark show={effectiveMandatory} />
                </label>

                {question.questionType === "table" ? (
                    <FormQuestionTable
                        renderTable={() => (
                            <TableManager
                                question={question}
                                tableId={qid}
                                answers={answers}
                                setAnswers={setAnswers}
                                evalCondition={evalCondition}
                                tripIndex={tripIndex}
                                onLoadDefinition={(def) =>
                                    setTableDefs((prev) => {
                                        if (
                                            prev[question.questionId] &&
                                            JSON.stringify(prev[question.questionId]) ===
                                            JSON.stringify(def)
                                        )
                                            return prev;
                                        return { ...prev, [question.questionId]: def };
                                    })
                                }
                                parentMandatory={effectiveMandatory}
                                parentQuestionNumber={questionNumber}
                                questionsList={questionsList}
                            />
                        )}
                    />
                ) : (
                    <QuestionInput
                        field={question}
                        value={currentValue}
                        onChange={handleQuestionValueChange}
                        onBlur={handleQuestionBlur}
                        onOptionsLoaded={(qid, opts) => {
                            setOptionsCache((prev) => {
                                if (JSON.stringify(prev[qid]) === JSON.stringify(opts))
                                    return prev;
                                return { ...prev, [qid]: opts };
                            });
                        }}
                    />
                )}

                {(question.questionType === "number" ||
                    originalQid === "number_of_trip_select") &&
                    parseInt(currentValue) > 0 &&
                    questionsList.some((child) =>
                        child.displayCondition?.some(
                            (c) => c.triggerQuestionId === originalQid,
                        ),
                    ) ? (
                    <div className="mt-6 space-y-6">
                        {Array.from(
                            { length: Math.min(parseInt(currentValue), 100) },
                            (_, i) => (
                                <div
                                    key={`${qid}_loop_${i}`}
                                    className="p-4 border-2 border-teal-100 rounded-xl bg-teal-50/10"
                                >
                                    <div className="text-teal-600 font-bold mb-4 uppercase text-sm tracking-wider">
                                        {originalQid.includes("trip") ||
                                            originalQid.includes("journey")
                                            ? "Trip"
                                            : "Entry"}{" "}
                                        {i + 1} {optionSuffix && `(${optionLabel})`}
                                    </div>
                                    {renderChildren(
                                        originalQid,
                                        formatSerialNumber(questionNumber, i + 1),
                                        depth + 1,
                                        i,
                                        optionSuffix,
                                        originalQid,
                                        effectiveMandatory,
                                    )}
                                </div>
                            ),
                        )}
                    </div>
                ) : (
                    renderChildren(
                        originalQid,
                        questionNumber,
                        depth + 1,
                        tripIndex,
                        optionSuffix,
                        repeatingParentId,
                        effectiveMandatory,
                    )
                )}
            </div>
        );
    };

    const renderQuestion = (
        question,
        depth = 0,
        tripIndex = null,
        optionSuffix = null,
        repeatingParentId = null,
        questionNumber = "",
        isParentMandatory = false,
        rootCounter = null,
    ) => {
        const isVisible = evalCondition(question, {
            tripIndex,
            optionSuffix,
            repeatingParentId,
        });
        if (!isVisible) return null;

        let resolvedQuestionNumber = questionNumber;
        if (depth === 0 && rootCounter) {
            if (isNumberedType(question.questionType)) {
                rootCounter.current += 1;
                resolvedQuestionNumber = String(rootCounter.current);
            } else {
                resolvedQuestionNumber = "";
            }
        }

        const multiselectTriggerId = getMultiselectDependency(
            question,
            answers,
            tripIndex,
            optionSuffix,
        );

        if (multiselectTriggerId) {
            const getVal = () => {
                let items;
                if (answers[multiselectTriggerId])
                    items = answers[multiselectTriggerId];
                if (!items) {
                    let s = optionSuffix;
                    while (!items) {
                        if (tripIndex !== null && s)
                            items = answers[`${multiselectTriggerId}_trip_${tripIndex}_${s}`];
                        if (!items && s) items = answers[`${multiselectTriggerId}_${s}`];
                        if (!items && tripIndex !== null)
                            items = answers[`${multiselectTriggerId}_trip_${tripIndex}`];
                        if (!items) items = answers[multiselectTriggerId];

                        if (items || !s || !s.includes(SUFFIX_SEP)) break;
                        s = s.substring(0, s.lastIndexOf(SUFFIX_SEP));
                    }
                }
                return items || [];
            };

            const actualSelectedItems = (getVal() || []).filter((v) => v !== "");

            if (actualSelectedItems.length > 0) {
                return (
                    <div key={`ms_${question.questionId}`} className="space-y-4">
                        {actualSelectedItems.map((itemValue) =>
                            renderQuestionInstance(
                                question,
                                depth,
                                tripIndex,
                                optionSuffix
                                    ? `${optionSuffix}${SUFFIX_SEP}${itemValue}`
                                    : itemValue,
                                repeatingParentId,
                                resolvedQuestionNumber,
                                isParentMandatory,
                            ),
                        )}
                    </div>
                );
            }
        }

        return renderQuestionInstance(
            question,
            depth,
            tripIndex,
            optionSuffix,
            repeatingParentId,
            resolvedQuestionNumber,
            isParentMandatory,
        );
    };

    // ============================================================================
    // PAYLOAD BUILDERS FOR DIFFERENT SUBSECTIONS
    // ============================================================================

    const buildStandardTablePayload = (qid, tableData, tableDef) => {
        if (!tableData || typeof tableData !== 'object') return null;

        const allRows = [];
        const tableFields = tableDef?.fields || [];

        const categoryField = tableFields.find(f =>
            f.fieldType === 'multiselect' &&
            (!f.displayCondition || f.displayCondition.length === 0)
        );
        const itemTypeFieldId = categoryField?.fieldId || 'item_type';

        const processRow = (row, itemType) => {
            if (!row || !row.values) return null;
            const processedValues = { [itemTypeFieldId]: itemType ? [itemType] : ["default"] };
            const usedKeys = new Set();

            tableFields.forEach(field => {
                if (field.fieldId === itemTypeFieldId) return;

                const isRepeaterTrigger = field.fieldType === 'number' && tableFields.some(other =>
                    other.displayCondition?.some(c => (c.triggerQuestionId === field.fieldId || c.triggerFieldId === field.fieldId) && c.operator === 'exists')
                );

                if (isRepeaterTrigger) {
                    const count = parseInt(row.values[field.fieldId]) || 0;
                    if (count > 0) {
                        const childFields = tableFields.filter(other =>
                            other.displayCondition?.some(c => (c.triggerQuestionId === field.fieldId || c.triggerFieldId === field.fieldId) && c.operator === 'exists')
                        );

                        const detailsArray = [];
                        for (let i = 0; i < count; i++) {
                            const detailObj = {};
                            childFields.forEach(cf => {
                                const loopKey = `${cf.fieldId}_loop_${i}`;
                                usedKeys.add(cf.fieldId);
                                if (row.values[loopKey] !== undefined && row.values[loopKey] !== null && row.values[loopKey] !== "") {
                                    let val = row.values[loopKey];
                                    const isNum = cf.fieldType === 'number' || cf.fieldType === 'percentage' || (!isNaN(Number(val)) && (cf.fieldId.includes("distance") || cf.fieldId.includes("fuel_consumption") || cf.fieldId.includes("quantity")));
                                    detailObj[cf.fieldId] = isNum ? Number(val) : val;
                                    usedKeys.add(loopKey);
                                }
                            });
                            if (Object.keys(detailObj).length > 0) {
                                detailsArray.push(detailObj);
                            }
                        }
                        if (detailsArray.length > 0) {
                            processedValues[`${field.fieldId}_details`] = detailsArray;
                        }
                    }
                }

                if (usedKeys.has(field.fieldId)) return;

                const directVal = row.values[field.fieldId];
                if (directVal !== undefined && directVal !== null && directVal !== "") {
                    const isNumberType = field.fieldType === 'number' || field.fieldType === 'percentage';
                    const looksNumeric = !isNaN(Number(directVal)) && (
                        field.fieldId.includes("quantity") ||
                        field.fieldId.includes("percentage") ||
                        field.fieldId.includes("amount") ||
                        (field.fieldLabel || "").toLowerCase().includes("percentage") ||
                        (field.fieldLabel || "").toLowerCase().includes("quantity")
                    );

                    if (isNumberType || looksNumeric) {
                        processedValues[field.fieldId] = Number(directVal);
                    } else {
                        processedValues[field.fieldId] = directVal;
                    }
                    usedKeys.add(field.fieldId);
                    return;
                }

                const collected = [];
                Object.keys(row.values).forEach(key => {
                    if (usedKeys.has(key)) return;
                    if (key.startsWith(`${field.fieldId}_`)) {
                        const suffix = key.replace(`${field.fieldId}_`, '');
                        let val = row.values[key];
                        if (val !== undefined && val !== null && val !== "") {
                            const isNumberType = field.fieldType === 'number' || field.fieldType === 'percentage';
                            const looksNumeric = !isNaN(Number(val)) && (
                                key.includes("quantity") ||
                                key.includes("percentage") ||
                                key.includes("amount") ||
                                (field.fieldLabel || "").toLowerCase().includes("percentage")
                            );

                            if (isNumberType || looksNumeric) {
                                val = Number(val);
                            }
                            collected.push({ key: suffix, value: val });
                            usedKeys.add(key);
                        }
                    }
                });

                if (collected.length > 0) {
                    processedValues[field.fieldId] = collected;
                }
            });

            Object.keys(row.values).forEach(key => {
                if (usedKeys.has(key)) return;
                if (processedValues[key] === undefined) {
                    const isSuffixedForKnownField = tableFields.some(f => key.startsWith(`${f.fieldId}_`));
                    if (!isSuffixedForKnownField) {
                        const val = row.values[key];
                        if (val !== undefined && val !== null && val !== "") {
                            const looksNumeric = !isNaN(Number(val)) && (
                                key.includes("quantity") ||
                                key.includes("percentage") ||
                                key.includes("amount") ||
                                key.includes("value") ||
                                key.includes("number")
                            );
                            processedValues[key] = looksNumeric ? Number(val) : val;
                        }
                    }
                }
            });

            return { rowId: row.rowId, values: processedValues };
        };

        if (Array.isArray(tableData)) {
            tableData.forEach(row => {
                const payload = processRow(row, null);
                if (payload) allRows.push(payload);
            });
        } else {
            Object.keys(tableData).forEach((itemType) => {
                const itemRows = tableData[itemType];
                if (Array.isArray(itemRows)) {
                    itemRows.forEach((row) => {
                        const payload = processRow(row, itemType);
                        if (payload) allRows.push(payload);
                    });
                }
            });
        }

        if (allRows.length === 0) return null;
        return {
            questionId: qid,
            questionType: "table",
            tableRows: allRows
        };
    };

    const buildMultiselectDependentNumberPayload = (qid, questionType, multiselectTriggerId) => {
        const selectedItems = answers[multiselectTriggerId] || [];
        if (!Array.isArray(selectedItems) || selectedItems.length === 0) return null;

        const collected = [];
        selectedItems.forEach(itemVal => {
            if (!itemVal) return;
            const suffixedKey = `${qid}_${itemVal}`;
            const val = answers[suffixedKey];
            if (val !== undefined && val !== null && val !== "") {
                const numericVal = Number(val);
                collected.push({
                    key: itemVal,
                    value: (questionType === 'number' || questionType === 'percentage')
                        ? (isNaN(numericVal) ? 0 : numericVal)
                        : val
                });
            }
        });

        return collected.length > 0
            ? { questionId: qid, questionType: questionType, value: collected }
            : null;
    };

    const buildBusinessTravelSubsectionPayload = () => {
        const formattedAnswers = [];

        const rootQids = ["total_number_of_trip", "type_of_business", "business_travel_returnmode_same", "return_no_selected", "return_destination", "no_selected"];
        rootQids.forEach(qid => {
            const val = answers[qid];
            if (val !== undefined && val !== null && (Array.isArray(val) ? val.length > 0 : val !== "")) {
                const q = questionsList.find(q => q.questionId === qid);
                if (q) {
                    const numericVal = Number(val);
                    formattedAnswers.push({
                        questionId: qid,
                        division: "business_travel_no_divison",
                        questionType: q.questionType,
                        value: q.questionType === 'number' ? (isNaN(numericVal) ? 0 : numericVal) : val
                    });
                }
            }
        });

        const returnTables = ["return_air_travel_type", "return_rail_travel_type", "return_maritime_travel_type", "return_road_travel_type"];
        returnTables.forEach(qid => {
            const val = answers[qid];
            if (val && typeof val === 'object') {
                const tableDef = tableDefs[qid];
                const payload = buildStandardTablePayload(qid, val, tableDef);
                if (payload) {
                    formattedAnswers.push({
                        ...payload,
                        division: "business_travel_no_divison"
                    });
                }
            }
        });

        const branches = [
            { countQid: "number_of_trip_individual", branchId: "individual_business_travel" },
            { countQid: "number_of_trip_group", branchId: "group_business_travel" }
        ];

        branches.forEach(({ countQid, branchId }) => {
            const countKey = `${countQid}_${branchId}`;
            const count = Number(answers[countKey]);
            if (!count || count <= 0) return;

            const detailsKey = `${countQid}_details`;
            const detailsArray = [];

            for (let i = 0; i < count; i++) {
                const tripKey = `${countQid}_${i + 1}`;
                const tripAnswers = [];
                const stateSuffix = `trip_${i}_${branchId}`;

                const tripChildQids = ["type_of_trip", "mode_transportation", "air_travel_type", "rail_travel_type", "maritime_travel_type", "road_travel_type"];

                tripChildQids.forEach(qid => {
                    const baseKey = `${qid}_${stateSuffix}`;
                    const relevantKeys = Object.keys(answers).filter(k => k === baseKey || k.startsWith(`${baseKey}${SUFFIX_SEP}`));

                    if (relevantKeys.length === 0) return;

                    relevantKeys.forEach(fullKey => {
                        const val = answers[fullKey];
                        if (val === undefined || val === null || (Array.isArray(val) ? val.length === 0 : val === "")) return;

                        const q = questionsList.find(q => q.questionId === qid);
                        if (!q) return;

                        if (q.questionType === "table") {
                            const tableDef = tableDefs[qid];
                            const payload = buildStandardTablePayload(qid, val, tableDef);
                            if (payload) {
                                const existingTable = tripAnswers.find(ta => ta.questionId === qid);
                                if (existingTable) {
                                    existingTable.tableRows = [...existingTable.tableRows, ...payload.tableRows];
                                } else {
                                    tripAnswers.push({
                                        questionId: qid,
                                        division: "business_travel_no_divison",
                                        questionType: "table",
                                        tableRows: payload.tableRows
                                    });
                                }
                            }
                        } else {
                            const existingIdx = tripAnswers.findIndex(ta => ta.questionId === qid);
                            if (existingIdx >= 0) {
                                if (Array.isArray(val) && Array.isArray(tripAnswers[existingIdx].value)) {
                                    tripAnswers[existingIdx].value = [...new Set([...tripAnswers[existingIdx].value, ...val])];
                                }
                            } else {
                                const numericVal = Number(val);
                                tripAnswers.push({
                                    questionId: qid,
                                    division: "business_travel_no_divison",
                                    questionType: q.questionType,
                                    value: q.questionType === 'number' ? (isNaN(numericVal) ? 0 : numericVal) : val
                                });
                            }
                        }
                    });
                });

                if (tripAnswers.length > 0) {
                    detailsArray.push({ [tripKey]: tripAnswers });
                }
            }

            formattedAnswers.push({
                questionId: countQid,
                division: "business_travel_no_divison",
                questionType: "number",
                value: count,
                [detailsKey]: detailsArray
            });
        });

        return formattedAnswers;
    };

    const buildFinalPayload = () => {
        let formattedAnswers = [];

        if (subSectionId === "business_travel") {
            formattedAnswers = buildBusinessTravelSubsectionPayload();
        } else {
            const processAnswer = (qid, key, val) => {
                const q = questionsList.find(q => q.questionId === qid);
                if (!q) return;
                let divisionId = "no division";
                if (divisionGroups && divisionGroups.length > 0) {
                    const division = divisionGroups.find(d =>
                        d.questions.some(dq => dq.questionId === qid)
                    );
                    if (division) {
                        divisionId = division.divisionId || division._id;
                    }
                }
                const tripMatch = key.match(/_trip_(\d+)/);
                let tripIndex = tripMatch ? parseInt(tripMatch[1]) : null;
                let optionValue = null;

                if (key !== qid) {
                    if (tripMatch) {
                        const afterTrip = key.substring(key.indexOf(`_trip_${tripIndex}_`) + `_trip_${tripIndex}_`.length);
                        if (afterTrip) optionValue = afterTrip;
                    } else {
                        optionValue = key.substring(qid.length + 1);
                    }
                }

                const multiselectTriggerId = getMultiselectDependency(q, answers);
                if (multiselectTriggerId && (q.questionType === "number" || q.questionType === "text" || q.questionType === "percentage") && !tripIndex) {
                    if (!optionValue) {
                        const payload = buildMultiselectDependentNumberPayload(qid, q.questionType, multiselectTriggerId);
                        if (payload) {
                            formattedAnswers.push({ ...payload, division: divisionId });
                        }
                    }
                    return;
                }

                if (val === undefined || val === null || val === "") return;

                if (q.questionType === "table") {
                    const tableData = val;
                    const tableDef = tableDefs[qid];
                    const payload = buildStandardTablePayload(qid, tableData, tableDef);
                    if (payload) {
                        formattedAnswers.push({ ...payload, tripIndex, optionValue, division: divisionId });
                    }
                } else {
                    const numericVal = Number(val);
                    const isExplicitNumber = q.questionType === "number" || q.questionType === "percentage";
                    const isImplicitNumber = !isNaN(numericVal) && (
                        key.includes("quantity") ||
                        key.includes("percentage") ||
                        key.includes("amount") ||
                        key.includes("value") ||
                        key.includes("number") ||
                        (q.questionText || "").toLowerCase().includes("percentage") ||
                        (q.questionText || "").toLowerCase().includes("quantity")
                    );

                    formattedAnswers.push({
                        questionId: qid,
                        questionType: q.questionType,
                        value: (isExplicitNumber || isImplicitNumber)
                            ? (isNaN(numericVal) ? 0 : numericVal)
                            : val,
                        optionValue: optionValue,
                        tripIndex: tripIndex,
                        division: divisionId
                    });
                }
            };

            questionsList.forEach((q) => {
                const qid = q.questionId;
                processAnswer(qid, qid, answers[qid]);
                const suffixedKeys = Object.keys(answers).filter(k => k.startsWith(`${qid}_`) && k !== qid);
                suffixedKeys.forEach(key => processAnswer(qid, key, answers[key]));
            });
        }

        return {
            UserId: "67625bbbc3a9701ba0386454",
            standardFormAllotmentId: "67b314afe7484a996e42998b",
            sectionId,
            subSectionId,
            answers: formattedAnswers,
        };
    };

    const validatePayload = (payload) => {
        const errors = [];
        const { answers: submittedAnswers } = payload;
        for (const ans of submittedAnswers) {
            const question = questionsList.find(q => q.questionId === ans.questionId);
            const label = question?.questionText || ans.questionId;
            const isPercentage = ans.questionType === "percentage" || String(ans.questionId).toLowerCase().includes("percentage");
            if (isPercentage) {
                if (Array.isArray(ans.value)) {
                    const total = ans.value.reduce((sum, item) => sum + (Number(item.value) || 0), 0);
                    if (total !== 0 && Math.abs(total - 100) > 0.1) {
                        errors.push(`Total percentage for "${label}" must be exactly 100%. Current: ${total}%`);
                    }
                } else {
                    const val = Number(ans.value);
                    if (val > 100) {
                        errors.push(`Percentage for "${label}" cannot exceed 100%.`);
                    }
                }
            }

            if (typeof ans.value === 'number' && ans.value < 0) {
                errors.push(`Negative values are not allowed for "${label}"`);
            }
        }
        return errors;
    };

    const submitForm = async () => {
        const payload = buildFinalPayload();

        const validationErrors = validatePayload(payload);
        if (validationErrors.length > 0) {
            validationErrors.forEach(err => toast.error(err));
            return;
        }

        try {
            const res = await postApi({ apiUrl: API_URLS.POSTALLQUESTIONS, data: payload });
            if (res && res.success) {
                toast.success("Form saved successfully!");
            } else {
                toast.error(res?.message || "Could not save your answers.");
            }
        } catch (e) {
            const status = e.response?.status;
            const serverMessage = e.response?.data?.message;

            if (status === 400 && serverMessage) {
                toast.error(serverMessage);
            } else if (status === 500) {
                toast.error("Something went wrong on our end. Please try again later.");
            } else if (!e.response) {
                toast.error("Percentage calculation is not correct or submission failed.");
            } else {
                toast.error("Action could not be completed at this time.");
            }
        }
    };

    const rootQuestions = useMemo(() => {
        const childIds = new Set();
        questionsList.forEach((q) => {
            if (q.displayCondition && q.displayCondition.length > 0) {
                childIds.add(q.questionId);
            }
        });

        return questionsList.filter((q) => !childIds.has(q.questionId));
    }, [questionsList]);

    const tripDependentQuestions = useMemo(() => {
        return questionsList.filter(q =>
            q.displayCondition?.some(c =>
                c.triggerQuestionId === "number_of_trip_select" &&
                c.operator === "exists"
            )
        );
    }, [questionsList]);

    const tripDependentIds = useMemo(() =>
        new Set(tripDependentQuestions.map(q => q.questionId)),
        [tripDependentQuestions]
    );

    const divisionGroups = useMemo(() => {
        if (!questionsProp || !Array.isArray(questionsProp)) return [];

        return questionsProp.map(division => ({
            divisionId: division.divisionId || division._id,
            divisionTitle: division.divisionTitle || division.name,
            totalQuestions: division.totalQuestions || (division.questions ? division.questions.length : 0),
            questions: division.questions || []
        }));
    }, [questionsProp]);

    const rootQuestionsByDivision = useMemo(() => {
        const childIds = new Set();
        questionsList.forEach((q) => {
            if (q.displayCondition && q.displayCondition.length > 0) {
                childIds.add(q.questionId);
            }
        });

        return divisionGroups.map((division) => ({
            ...division,
            rootQuestions: division.questions.filter(
                (q) => !childIds.has(q.questionId),
            ),
        }));
    }, [divisionGroups, questionsList]);

    const [expandedDivisions, setExpandedDivisions] = useState({});

    useEffect(() => {
        const initialExpanded = {};
        divisionGroups.forEach((group, idx) => {
            const key = group.divisionId || group.divisionTitle || idx;
            initialExpanded[key] = true;
        });
        setExpandedDivisions(initialExpanded);
    }, [divisionGroups]);

    const toggleDivision = (key) => {
        setExpandedDivisions((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
            <div className="max-w-4xl mx-auto">
                {isLoadingAnswers ? (
                    <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl shadow-sm border border-gray-100">
                        <div className="w-12 h-12 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin mb-4"></div>
                        <p className="text-gray-500 font-medium">Loading saved answers...</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="space-y-4">
                            {divisionGroups.length > 0 ? (
                                divisionGroups.map((group, idx) => {
                                    const divisionKey = group.divisionId || group.divisionTitle || idx;
                                    const isExpanded = expandedDivisions[divisionKey] !== false;
                                    const groupQuestions = rootQuestionsByDivision[idx]?.rootQuestions || [];
                                    const rootCounter = { current: 0 };

                                    return (
                                        <div key={group.divisionId || idx} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                                            <button
                                                type="button"
                                                onClick={() => toggleDivision(divisionKey)}
                                                className={`w-full flex items-center justify-between px-6 py-4 text-left transition-colors duration-200 ${isExpanded ? "bg-teal-50" : "hover:bg-gray-50"
                                                    }`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <span className={`font-semibold text-lg ${isExpanded ? "text-teal-700" : "text-gray-700"}`}>
                                                        {group.divisionTitle || "No Division"}
                                                    </span>
                                                </div>
                                                <svg
                                                    className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${isExpanded ? "transform rotate-180" : ""}`}
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </button>

                                            {isExpanded && (
                                                <div className="p-6 border-t border-gray-100 bg-white animate-fadeIn">
                                                    {groupQuestions.length > 0 ? (
                                                        groupQuestions.map((q) =>
                                                            renderQuestion(
                                                                q,
                                                                0,
                                                                null,
                                                                null,
                                                                null,
                                                                "",
                                                                false,
                                                                rootCounter,
                                                            ),
                                                        )
                                                    ) : (
                                                        <div className="text-center text-gray-500 py-4">
                                                            No questions found in this division.
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            ) : (
                                (() => {
                                    const rootCounter = { current: 0 };
                                    return rootQuestions.length > 0 ? (
                                        rootQuestions.map((q) =>
                                            renderQuestion(
                                                q,
                                                0,
                                                null,
                                                null,
                                                null,
                                                "",
                                                false,
                                                rootCounter,
                                            ),
                                        )
                                    ) : (
                                        <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
                                            No questions found in this section.
                                        </div>
                                    );
                                })()
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};